import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form990Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 990 Instructions and Filing Guide for Nonprofits"
          description="Learn about IRS Form 990: who must file, deadlines, and detailed line-by-line instructions for a smooth filing process. E-file with TaxZerone today!"
          keywords=""
          canonical="resources/tax-exempt/form-990-instructions/"
          ogType="article"
          jsonLdPricing="9.90"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 990</span>
          </span>{' '}
          Instructions: Complete Filing Guide for <br /> Tax-Exempt
          Organizations
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Everything you need to file accurately, on time, and avoid IRS
          penalties for tax years 2023, 2024, and 2025.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990instructions`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-990-instructions/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 990 at a Glance
                  </Typography>
                  <Typography
                    variant="body1"
                    itemProp="description"
                    sx={{ mb: 2 }}
                  >
                    <Link
                      href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990/"
                      sx={{ color: '#0202ff', textDecoration: 'underline' }}
                    >
                      IRS Form 990
                    </Link>{' '}
                    is the annual information return for eligible tax-exempt
                    organizations to report their financial activities,
                    governance, and operations. Organizations with{' '}
                    <span style={{ fontWeight: 600 }}>
                      gross receipts greater than $200,000 or total assets
                      greater than $500,000
                    </span>{' '}
                    generally must file Form 990. Smaller eligible organizations
                    may qualify to{' '}
                    <Link
                      href="https://testweb.variniral.com/tax-exempt/e-file-irs-form-990-ez/"
                      sx={{ color: '#0202ff', textDecoration: 'underline' }}
                    >
                      file Form 990-EZ
                    </Link>{' '}
                    or{' '}
                    <Link
                      href="https://test990n.variniral.com/?ref=menu"
                      sx={{ color: '#0202ff', textDecoration: 'underline' }}
                    >
                      Form 990-N(e-postcard)
                    </Link>
                    .
                  </Typography>
                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <Link
                          href="/resources/tax-exempt/form-990-due-dates/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          <b>Due Date:</b>
                        </Link>{' '}
                        <b>15th day of the 5th month</b> after the end of the
                        tax year (May 15 for most calendar-year filers).
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <Link
                          href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          <b>Extension:</b>
                        </Link>{' '}
                        File <b>IRS Form 8868</b> for automatic{' '}
                        <b>6-month extension.</b>
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <Link
                          href="/resources/tax-exempt/form-990-penalties/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          <b>Late Filing Penalties:</b>
                        </Link>{' '}
                        Late or incomplete returns may result in IRS penalties,
                        and failing to file for <b>three consecutive years</b>{' '}
                        may result in the{' '}
                        <Link
                          href="/resources/tax-exempt/automatic-revocation-of-tax-exempt-status/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          automatic revocation
                        </Link>{' '}
                        of tax-exempt status.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Box>
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                      fontWeight: 600,
                      mb: 0.5,
                    }}
                  >
                    What is IRS Form 990?
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    <span style={{ fontWeight: 600 }}>
                      <Link
                        href="/tax-exempt/e-file-irs-form-990/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        IRS Form 990
                      </Link>
                    </span>
                    , officially titled{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      &quot;
                      <i>Return of Organization Exempt from Income Tax</i>
                      ,&quot;
                    </span>{' '}
                    is an annual information return required to be filed with
                    the Internal Revenue Service (IRS) by most tax-exempt
                    organizations, including charities and non-profit groups.
                    The form provides the IRS and the public with financial
                    information about the organization, allowing for greater
                    transparency and accountability.
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 4 }}>
                    The Form 990 return includes details about the
                    organization&apos;s mission, programs, and finances,
                    including income, expenses, assets, and liabilities.
                  </Typography>
                </Box>
                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: '18px !important',
                          fontWeight: 600,
                          mb: 1,
                          lineHeight: 1.5,
                        }}
                      >
                        File your 990 series returns fast with TaxZerone and get
                        10% off—use code{' '}
                        <Box
                          component="strong"
                          sx={{
                            ml: 0,
                            color: '#c02652',
                            fontWeight: 600,
                            border: '2px dashed rgb(211, 163, 33)',
                            px: 1,
                            backgroundColor: 'rgb(255, 250, 205)',
                          }}
                        >
                          TZOTE10
                        </Box>{' '}
                        at checkout!
                      </Typography>
                      <Typography variant="body2">
                        We support tax years 2025, 2024, and 2023.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990instructions`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who must File Form 990?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  To file{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 990
                  </Link>
                  , your exempt organization must meet the following criteria:
                </Typography>
                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Tax-Exempt Status:</b> Organizations that are exempt
                      from federal income tax under section 501(a) and fall
                      under sections 501(c), 527, or 4947(a)(1) are generally
                      required to file Form 990.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Gross Receipts and Assets:</b> Your organization&apos;s{' '}
                      <b>gross receipts</b> for the year must be{' '}
                      <b>greater than $200,000</b>, and your <b>total assets</b>{' '}
                      at the end of the tax year must be{' '}
                      <b>greater than $500,000</b>.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is Form 990 due?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>IRS Form 990</strong> is due on the{' '}
                  <b>15th day of the 5th month</b> after the end of the
                  organization&apos;s tax year. For calendar year filers, this
                  means Form 990 is due on <b>May 15th</b> of the following
                  year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  However, if the due date falls on a weekend or a legal
                  holiday, the filing deadline is extended to the next business
                  day. Extensions can be requested if needed.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 990 penalty
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The IRS imposes penalties based on the organization&apos;s
                  annual gross receipts:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Gross Receipts
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Penalty
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,

                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Maximum Penalty
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                              fontWeight: 600,
                            }}
                          >
                            Up to $1,274,000
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $25</span> per
                            day for failure to file, starting from the due date.
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              backgroundColor: '#fff',
                            }}
                          >
                            Lesser of{' '}
                            <span style={{ fontWeight: 600 }}>$12,000</span> or{' '}
                            <span style={{ fontWeight: 600 }}>5% </span>of gross
                            receipts for the year
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                              fontWeight: 600,
                            }}
                          >
                            Above $1,274,000
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $125 </span> per
                            day for failure to file, starting from the due date.
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              backgroundColor: '#fff',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}>$63,500</span>{' '}
                            maximum penalty for one return
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: '18px !important',
                          fontWeight: 600,
                          mb: 1,
                          lineHeight: 1.4,
                        }}
                      >
                        Facing a 990 filing deadline? Get a stress-free
                        extension!
                      </Typography>
                      <Typography variant="body2">
                        E-file Form 8868 and get an extension of up to 6 months
                        to file your 990 return.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990instructions`}
                      >
                        E-file Form 8868
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> The penalty can also be charged if the
                  organization files an incomplete return, such as by failing to
                  complete a required line item or a required part of a
                  schedule.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 990 Instructions - How to fill out?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Here&apos;s a complete guide on how to fill out IRS Tax Form
                  990.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This section requires information about your organization,
                  including name, address, accounting method, tax-exempt status,
                  and tax period.
                </Typography>
                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  Keeping the previous year&apos;s filed irs 990 return in hand
                  can help to complete your filing process.
                </Alert>
                <picture>
                  <source srcSet="/assets/images/f990-org-details-img.webp" />
                  <source srcSet="/assets/images/f990-org-details-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-org-details-img.png"
                    loading="lazy"
                    alt="Organization details of 990"
                    width={760}
                    height={292}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item A - Tax period.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This section collects information about your
                  organization&apos;s tax period.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select the calendar year option if your organization follows a
                  calendar tax year (January 01 - December 31)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If your organization follows a fiscal tax year, choose the{' '}
                  <b>Tax Period Begin Date</b> and <b>Tax Period End Date</b>.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item B - Applicable Checkboxes
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check the appropriate boxes to indicate changes on the current
                  return compared with the previously filed 990 return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b> Address Change</b> - Check the box if the organization
                  changed its address and didn&apos;t report the change on the
                  previously filed Form 990.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b> Final Return</b> - Check the box if the organization has
                  terminated its existence or ceased to be a Section 501(a) or
                  Section 527 organization and is filing its final return as an
                  exempt organization or Section 4947(a)(1) trust. You may also
                  need to attach{' '}
                  <b>
                    <i>Schedule N</i>
                  </b>{' '}
                  along with this final 990 return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <b> Name Change</b> - Check the box if the organization
                  changed its legal name (not its “DBA” name) and didn&apos;t
                  report the change on the previously filed 990 return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You must also attach <b>supporting documents</b> for the name
                  change based on your organization&apos;s type.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <b> Amended Return</b> - Check the box if the organization is
                  filing an amended return for the tax year to amend the
                  previously filed return for the same tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You must also attach Schedule O and explain which parts,
                  schedules, or attachments of Form 990 were amended and
                  describe those amendments.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b> Initial Return</b> - Check the box if the organization is
                  filing a Form 990 return for the first time and hasn&apos;t
                  previously filed a{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 990-EZ
                  </Link>
                  ,{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-pf/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-PF
                  </Link>
                  ,{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-t/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-T
                  </Link>
                  , or{' '}
                  <Link
                    href="https://epostcard.taxzerone.com/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-N{' '}
                  </Link>{' '}
                  return
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b> Application Pending</b> - Check the box if the
                  organization has either filed a Form 1023, 1023-EZ, 1024, or
                  1024-A with the IRS and is awaiting a response, or claims
                  tax-exempt status under section 501(a) but hasn&apos;t filed
                  Form 1023, 1023-EZ, 1024, or 1024-A to be recognized as
                  tax-exempt by the IRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  If this box is checked, you must complete all parts of Form
                  990 and any required schedules.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item C - Organization&apos;s name and address
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the organization&apos;s legal name on the “Name of
                  organization” line. If the organization operates under a name
                  different from its legal name, enter the alternate name on the
                  “Doing Business As” (DBA) line
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For foreign addresses, enter the information in the following
                  order: city or town, state or province, the name of the
                  country, and the postal code.
                </Typography>
                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  If a change of address occurs after the return is filed, use
                  Form 8822-B to notify the IRS of the new address.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item D - Organization&apos;s EIN
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the Employer Identification Number (EIN) provided for
                  your organization.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If the filing organization has more than one EIN and
                  hasn&apos;t been advised which to use, send notice to
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Department of the Treasury
                  <br /> Internal Revenue Service Center
                  <br /> Ogden, UT 84201-0027
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item F - Name and address of principal officer
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the name and complete mailing address of the principal
                  officer. If the officer prefers to be contacted at the
                  organization&apos;s address listed in Item C, enter “same as C
                  above.”
                </Typography>
                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  If a change in responsible party occurs after the return is
                  filed, use Form 8822-B to notify the IRS of the new
                  responsible party.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item G - Gross Receipts $
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter your organization&apos;s Gross receipts for the tax
                  year.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item H - Group Returns
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    If the organization answers “No” to Item H(a), it
                    shouldn&apos;t check a box in Item H(b).
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    If the organization answers “Yes” to Item H(a) but “No” to
                    Item H(b), attach a list (not on Schedule O (Form 990))
                    showing the name, address, and EIN of each local or
                    subordinate organization included in the group return.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Additionally, attach a list (not on Schedule O) showing the
                  name, address, and EIN of each subordinate organization not
                  included in the group return.
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 12 }}>
                    If the organization answers “Yes” to Item H(a) and “Yes” to
                    Item H(b), attach a list (not on Schedule O) showing the
                    name, address, and EIN of each subordinate organization
                    included in the group return.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item I - Tax-Exempt Status
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Tax-exempt status provides an organization with complete
                  relief from paying taxes. In Item I, choose the
                  organization&apos;s tax-exempt status.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the organization is exempt under section 501(c) (other than
                  section 501(c)(3)), check the second box and insert the
                  appropriate subsection number within the parentheses (for
                  example, “4” for a section 501(c)(4) organization).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item J - Website
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter your organization&apos;s website name and include the
                  website link. If the organization doesn&apos;t maintain a
                  website, enter “N/A” (not applicable).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item K - Form of Organization
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  A Legal Entity Form includes corporations, trusts,
                  unincorporated associations, and other entities (for example,
                  partnerships and limited liability companies (LLCs)) with
                  legal rights and responsibilities, including tax filings.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Choose the organization&apos;s legal entity; whether it&apos;s
                  a <b>Corporation, Trust, Association</b>, or <b>Other</b>. If
                  Other is selected, please explain that form of organization.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item L - Year of formation
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the year in which the organization was legally created
                  under state or foreign law. If a corporation, enter the year
                  of incorporation.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item M - State of legal domicile
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  For a corporation, enter the state of incorporation (country
                  of incorporation for a foreign corporation formed outside the
                  United States). For a trust or other entity, enter the state
                  whose law governs the organization&apos;s internal affairs (or
                  the foreign country whose law governs a foreign organization
                  other than a corporation).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part I - Summary
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part1-line1-7-img.webp" />
                  <source srcSet="/assets/images/f990-part1-line1-7-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part1-line1-7-img.png"
                    loading="lazy"
                    alt="Form 990 Part I, Lines 1–7 showing organization activities and governance information"
                    width={760}
                    height={197}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 990 Part I consists of 4 sections
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>Activities & Governance</li>
                  <li style={{ marginBottom: 12 }}>Revenue</li>
                  <li style={{ marginBottom: 12 }}>Expenses</li>
                  <li style={{ marginBottom: 12 }}>
                    Net Assets or Fund Balances
                  </li>
                </ul>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Activities & Governance
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1</span> - In this
                  line, briefly describe the organization&apos;s mission or its
                  most significant activities for the year, whichever the
                  organization wishes to highlight.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 2</span> - Select “Yes”
                  if the organization answered “Yes” on Part IV, line 31, or 32.
                  If your organization selected Yes, it means it discontinued
                  its operations or disposed of more than 25% of its net assets.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You must also complete <b>Schedule N</b>, Liquidation,
                  Termination, Dissolution, or Significant Disposition of Assets
                  (Form 990), Part I or Part II.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 3</span> - In this
                  line, enter the number of voting members of the governing
                  body.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 4</span> -In this line,
                  enter the number of independent voting members of the
                  governing body.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 5</span> -In line 5,
                  enter the total number of individuals employed in the calendar
                  year 2025.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 6 - Total number of volunteers
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In line 6, enter the number of volunteers, full-time and
                  part-time, including volunteer members of the
                  organization&apos;s governing body, who provided volunteer
                  services to the organization during the reporting year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 7a - Total unrelated business revenue
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter in line 7a the total unrelated business revenue from
                  Part VIII, column (C), line 12.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 7b - Net unrelated business taxable income
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the organization’s net unrelated business taxable income
                  for the year and{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-t/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 990-T
                  </Link>
                  . If the organization isn&apos;t required to file a Form 990-T
                  for the tax year, enter “0.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the organization still needs to file Form 990-T for the tax
                  year, provide an estimate of the amount it expects to report
                  on Form 990-T, Part I, line 11 when it is filed.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Revenue and Expenses (Line 8 - 19)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part1-line8-19-img.webp" />
                  <source srcSet="/assets/images/f990-part1-line8-19-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part1-line8-19-img.png"
                    loading="lazy"
                    alt="Form 990 Part I, Lines 8–19 showing revenue and expense details"
                    width={760}
                    height={233}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your organization is filing Form 990 as an initial return,
                  or if it filed Form 990-EZ or 990-PF in the prior year, leave
                  the “Prior Year” column blank. Use the same lines from the
                  previous year&apos;s Form 990 to determine what to report for
                  the prior year&apos;s revenue and expense amounts.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 16a - Professional fundraising fees
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  In this line, enter the total of
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    the fees for professional fundraising services reported in
                    Part IX, column (A), line 11e; and
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    the portion of the amount reported in Part IX, column (A),
                    lines 5 and 6, comprises fees for professional fundraising
                    services paid to officers, directors, trustees, key
                    employees, and disqualified persons, whether or not such
                    persons are employees of the organization.
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  However, don&apos;t report the latter amount from Part I, line
                  15.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 16b - Total fundraising expenses
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In this line, enter the total fundraising expenses reported in
                  Part IX, column (D), line 25.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 2,
                    lineHeight: 1.4,
                  }}
                >
                  Net Assets or Fund Balances
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part1-line20-22-img.webp" />
                  <source srcSet="/assets/images/f990-part1-line20-22-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part1-line20-22-img.png"
                    loading="lazy"
                    alt="Form 990 Part I, Lines 20–22 showing net assets or fund balances "
                    width={760}
                    height={66}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 20 - Total assets
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In this line, enter the value of the total assets from Part X,
                  line 16
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 21 - Total liabilities
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In this line, enter the value of the total liabilities from
                  Part X, line 26
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 22 - Net assets or fund balances
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  To calculate the Net assets or fund balances, subtract line 21
                  from line 20 and enter the value in this line.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 2,
                    lineHeight: 1.4,
                  }}
                >
                  Part II - Signature Block
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part2-sign-img.webp" />
                  <source srcSet="/assets/images/f990-part2-sign-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part2-sign-img.png"
                    loading="lazy"
                    alt="Form 990-part-II signature block"
                    width={760}
                    height={197}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  To complete this part, your 990 return must be signed by the
                  current president, vice president, treasurer, assistant
                  treasurer, chief accounting officer, or other corporate
                  officer (such as a tax officer) authorized to sign as of the
                  date this 990 return is filed.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Paid Preparer
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  However, if the organization hired an individual to complete
                  the return on their behalf, the return must signed by that
                  individual (who is the Paid preparer), and the organization
                  must list the preparer&apos;s taxpayer identification number
                  (PTIN), and fill in the other blanks in the Paid Preparer Use
                  Only area.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The paid preparer must
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Sign the return in the space provided for the
                    preparer&apos;s signature;{' '}
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Enter the preparer&apos;s information, including the
                    preparer&apos;s PTIN; and
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Give a copy of the return to the organization
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check “Yes” if the IRS can contact the paid preparer who
                  signed the return to discuss the return. This authorization
                  applies only to the individual whose signature appears in the
                  Paid Preparer Use Only section of Form 990.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part III - Statement of Program Service Accomplishments
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part3-statement-img.webp" />
                  <source srcSet="/assets/images/f990-part3-statement-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part3-statement-img.png"
                    loading="lazy"
                    alt="Form 990-part-III (Statement of Program Service Accomplishments) Line 1-4"
                    width={760}
                    height={277}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 1 - Organization&apos;s mission
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In this line, describe the organization&apos;s mission as
                  articulated in its mission statement or as otherwise adopted
                  by the organization&apos;s governing body, if applicable.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 2</span> - Select “Yes”
                  if the organization undertook any significant program services
                  during the year that were not listed on the prior{' '}
                  <span style={{ fontWeight: 600 }}> Form 990</span> or
                  <span style={{ fontWeight: 600 }}> 990-EZ </span>return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If selected Yes, describe those items on{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Schedule O (Form 990)
                  </span>
                  . If any are among the activities described on Form 990, Part
                  III, line 4, the organization can reference the detailed
                  description on line 4.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  However, if the organization has never filed a Form 990 or
                  990-EZ, select “No.”
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 3</span> - Select “Yes”
                  if the organization made any significant changes before the
                  end of the tax year in how it conducts its program services to
                  further its exempt purposes, or if the the organization ceased
                  conducting significant program services that had been
                  conducted in a prior year. Also, describe these items on
                  Schedule O (Form 990).
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> Don&apos;t report a fundraising activity as a
                  program service accomplishment unless it is substantially
                  related to the accomplishment of the organization&apos;s
                  exempt purposes (other than by raising funds).
                </Alert>
                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  An organization must report new, significant program services,
                  or significant changes in how it conducts program services on
                  its Form 990, Part III, rather than in a letter to IRS Exempt
                  Organizations Determinations (“EO Determinations”).
                </Alert>
                <picture>
                  <source srcSet="/assets/images/f990-part3-line4a-4c-img.webp" />
                  <source srcSet="/assets/images/f990-part3-line4a-4c-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part3-line4a-4c-img.png"
                    loading="lazy"
                    alt="Form 990 Part III, Lines 4a–4c showing program codes, expenses including grants, & related revenue"
                    width={760}
                    height={646}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 4a-4c</span> - In these
                  lines, the filing organization must describe their
                  accomplishments for each of their{' '}
                  <strong>three largest program services</strong>, as measured
                  by total expenses incurred (not including donated services or
                  the donated use of materials, equipment, or facilities).
                  Describe each program service activity if there were three or
                  fewer such activities.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The organization can report on <i>Schedule O (Form 990)</i>
                  additional activities that it considers of comparable or
                  greater importance, although smaller in terms of expenses
                  incurred (such as activities conducted with volunteer labor).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If your organization is under Section 501(c)(3) and 501(c)(4),
                  you’re required to report the amount of grants and allocations
                  to others, the total expenses, and revenue, if any, for each
                  program service reported.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part3-line4d-4e-img.webp" />
                  <source srcSet="/assets/images/f990-part3-line4d-4e-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part3-line4d-4e-img.png"
                    loading="lazy"
                    alt="Form 990 Part III Lines 4d–4e other program services and total expenses"
                    width={760}
                    height={50}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 4d - Other program services
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In this line, enter the organization&apos;s other program
                  services. The detailed description required for the three
                  largest program services need not be provided for these other
                  program services.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  However, you’re required to report the amount of grants and
                  allocations to others, the total expenses, and revenue, if
                  any, for each program service reported.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 4e – Total Program Service Expenses
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  On Line 4e, enter the total amount your organization spent on
                  program services during the tax year. This should reflect only
                  the costs directly related to carrying out your nonprofit’s
                  mission.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part IV - Checklist of Required Schedules
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part4-line1-3-img.webp" />
                  <source srcSet="/assets/images/f990-part4-line1-3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part4-line1-3-img.png"
                    loading="lazy"
                    alt="Form 990 Part IV, Lines 1–3 covering filing requirements, contributor reporting, and political activity disclosures"
                    width={760}
                    height={115}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1</span> - Select
                  &quot;Yes&quot; if the organization is a section 501(c)(3)
                  organization that isn&apos;t a private foundation and claims
                  section 501(c)(3) status but hasn&apos;t yet filed a Form 1023
                  or Form 1023-EZ application or received a determination letter
                  recognizing its section 501(c)(3) status.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you have selected “Yes,” you must complete and attach
                  <Link
                    href="/resources/tax-exempt/form-990-schedule-a/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Schedule A
                  </Link>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 2</span> - Select “Yes”
                  if your organization satisfies any of the following
                  conditions.
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    A section 501(c)(3) organization met the 331/3% support test
                    of the regulations under sections 509(a)(1) and
                    170(b)(1)(A)(vi); checks the box on{' '}
                    <span style={{ fontWeight: 600 }}>
                      Schedule A (Form 990)
                    </span>
                    , Part II, line 13, 16a, or 16b; and received from any one
                    contributor, during the year, contributions of the greater
                    of $5,000 (in money or property) or 2% of the amount on Form
                    990, Part VIII, line 1h. An organization filing Schedule B
                    (Form 990) can limit the contributors it reports on Schedule
                    B (Form 990) using this greater-than-$5,000/2% threshold
                    only if it checks the box on Schedule A (Form 990), Part II,
                    line 13, 16a, or 16b.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    A section 501(c)(3) organization didn’t meet the 331/3%
                    support test of the regulations under sections 509(a)(1) and
                    170(b)(1)(A)(vi), and received during the year contributions
                    of $5,000 or more from any one contributor.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    A section 501(c)(7), 501(c)(8), or 501(c)(10) organization
                    received, during the year, (a) contributions of any amount
                    for use exclusively for religious, charitable, scientific,
                    literary, or educational purposes, or for the prevention of
                    cruelty to children or animals; or (b) contributions of
                    $5,000 or more not exclusively for such purposes from any
                    one contributor.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Any other organization that received, during the year,
                    contributions of{' '}
                    <span style={{ fontWeight: 600 }}>$5,000 or more</span> from
                    any one contributor
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If any one of the above conditions is satisfied, the
                  organization must complete and attach{' '}
                  <Link
                    href="/resources/tax-exempt/form-990-schedule-b/"
                    sx={{ color: '#0202ff' }}
                  >
                    Schedule B
                  </Link>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 3</span> - Select “Yes”
                  if the organization engaged in direct or indirect political
                  campaign activities activity which were conducted directly or
                  indirectly through a disregarded entity or a joint venture or
                  other arrangement treated as a partnership for federal income
                  tax purposes and in which the organization is an owner. If
                  Yes, the organization must complete Schedule C, Part I.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> All organizations must answer this question,
                  even if they aren&apos;t subject to a prohibition against
                  political campaign activities.
                </Alert>
                <picture>
                  <source srcSet="/assets/images/f990-part4-line4-8-img.webp" />
                  <source srcSet="/assets/images/f990-part4-line4-8-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part4-line4-8-img.png"
                    loading="lazy"
                    alt=" Form 990 Part IV Lines 4–8 special activity and asset disclosure questions"
                    width={760}
                    height={182}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 4</span> - Complete
                  this line only if your organization is a section 501(c)(3)
                  organization. Other organizations leave this line blank.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select “Yes” if the organization engaged in lobbying
                  activities or had a section 501(h) election in effect during
                  the tax year. If Yes, the organization must complete Schedule
                  C, Part II.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> All section 501(c)(3) organizations that had a
                  section 501(h) election in effect during the tax year must
                  complete Schedule C (Form 990), Part II-A, whether or not they
                  engaged in lobbying activities during the tax year.
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 5</span> - Select “Yes”
                  only if the organization is a section 501(c) (4), 501(c)(5),
                  or 501(c)(6) organization that receives membership dues,
                  assessments, or similar amounts. If Yes, the organization must
                  complete Schedule C, Part III.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 6</span> - Select “Yes”
                  if the organization maintained at any time during the
                  organization&apos;s tax year a donor advised fund or another
                  similar fund or account. If Yes, the organization must
                  complete and attach Schedule D, Part I.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 7</span> - Select “Yes”
                  if the organization received or held any conservation easement
                  at any time during the year, regardless of how the
                  organization acquired the easement or whether a charitable
                  deduction was claimed by a donor of the easement. If Yes, the
                  organization must complete and attach Schedule D, Part II.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 8</span> - Select “Yes”
                  if, at any time during the year, the organization maintained
                  collections of works of art, historical treasures, and other
                  similar assets. If Yes, the organization must complete and
                  attach Schedule D, Part III.
                </Typography>
                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  Organizations that answer “Yes” on line 8 will often answer
                  “Yes” on Part IV, line 30, which addresses current-year
                  noncash contributions of such items.
                </Alert>
                <picture>
                  <source srcSet="/assets/images/f990-part4-line9-11-img.webp" />
                  <source srcSet="/assets/images/f990-part4-line9-11-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part4-line9-11-img.png"
                    loading="lazy"
                    alt="Form 990 Part IV Lines 9–11f asset, liability, and endowment disclosure questions"
                    width={760}
                    height={298}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 9</span> - Select “Yes”
                  if, at any time during the organization&apos;s tax year, the
                  organization
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    had an escrow or custodial account;
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    provided credit counseling services and/or debt management
                    plan services, such as credit repair or debt negotiations;
                    or
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    acted as an agent, trustee, custodian, or other intermediary
                    for contributions or other assets not included in Part X
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If selected Yes to any one of the conditions, the organization
                  must complete and attach Schedule D, Part IV.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 10</span> - Select
                  “Yes” if the organization, a related organization, or an
                  organization formed and maintained exclusively to further one
                  or more exempt purposes of the organization held assets in
                  donor-restricted endowment funds, board-designated (quasi), or
                  endowment funds at any time during the year, whether or not
                  the organization follows ASC 958, or reports endowment funds
                  in Part X, line 31.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the organization selected Yes, it must complete and attach
                  Schedule D, Part IV.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 11</span> - contains a
                  collection of “Yes” or “No” questions the organization needs
                  to answer.
                </Typography>{' '}
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 11a</span> - Select
                  “Yes” if the organization reported an amount for land,
                  buildings, and equipment in Part X, line 10. If selected
                  “Yes,” the organization must complete Schedule D, Part VI.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 11b</span> - Select
                  “Yes” if the organization reported an amount for
                  investments—other securities in Part X, line 12, that is 5% or
                  more of its total assets reported in Part X, line 16. If
                  selected “Yes,” the organization must complete Schedule D,
                  Part VII.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 11c</span> - Select
                  “Yes” if the organization reported an amount for investments -
                  program related in Part X, line 13, that is 5% or more of its
                  total assets reported in Part X, line 16. If selected “Yes,”
                  the organization must complete Schedule D, Part VIII.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 11d</span> - Select
                  “Yes” if the organization reported an amount for other assets
                  in Part X, line 15, that is 5% or more of its total assets
                  reported in Part X, line 16. If selected “Yes,” the
                  organization must complete Schedule D, Part IX.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 11e</span> - Select
                  “Yes” if the organization reported an amount for other
                  liabilities in Part X, line 25. If selected “Yes,” the
                  organization must complete Schedule D, Part X.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 11f</span> - Select
                  “Yes” if the organization’s separate or consolidated financial
                  statements for the tax year included a footnote that addresses
                  the organization’s liability for uncertain tax positions under
                  FIN 48 (ASC 740). If selected “Yes,” the organization must
                  complete Schedule D, Part X.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part4-line12-14-img.webp" />
                  <source srcSet="/assets/images/f990-part4-line12-14-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part4-line12-14-img.png"
                    loading="lazy"
                    alt="Form 990 Part IV, Lines 12a–14b covering audited financial statements, school status, & foreign activities"
                    width={760}
                    height={148}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 12a</span> - Select
                  “Yes” if the organization received separate, independent
                  audited financial statements for the year for which it is
                  completing this return, or if the organization is reporting
                  for a short year that is included in, but not identical to,
                  the period for which the audited financial statements were
                  obtained.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If selected “Yes,” the organization must complete Schedule D,
                  Part XI and XII.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 12b</span> - Select
                  “Yes” if the organization was included in consolidated,
                  independent audited financial statements for the year for
                  which it is completing this return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  However, if the organization answered “Yes,” to line 12b and
                  answered “No” to line 12a, then completing Schedule D, Parts
                  XI and XII is optional.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 13</span> - Select
                  “Yes” if the organization checked the box on Schedule A (Form
                  990), Part I, line 2, indicating that it is a school. If
                  selected “Yes,” the organization must complete and attach
                  Schedule E.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 14a</span> - Select
                  “Yes” if the organization maintained an office, or had
                  employees or agents, or independent contractors outside the
                  United States.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 14b</span> - Select
                  “Yes” if the organization had aggregate revenue or expenses of
                  more than $10,000 from or attributable to grantmaking,
                  fundraising activities, business, investment, and program
                  service activities outside the United States, or if the book
                  value of the organization&apos;s aggregate investments in
                  foreign partnerships, foreign corporations, and other foreign
                  entities was $100,000 or more at any time during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If selected “Yes,” the organization must complete Schedule F,
                  Parts I and IV.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part4-line15-19-img.webp" />
                  <source srcSet="/assets/images/f990-part4-line15-19-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part4-line15-19-img.png"
                    loading="lazy"
                    alt="Form 990 Part IV Lines 15–19 foreign assistance & fundraising disclosures"
                    width={760}
                    height={164}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 15</span> - Select
                  “Yes” if the organization reported on Part IX, line 3, column
                  (A), more than $5,000 of grants and other assistance to any
                  foreign organization or entity (including a foreign
                  government), or to a domestic organization or domestic
                  individual. If “Yes,” the organization must complete Schedule
                  F, Parts II and IV.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 16</span> - Select
                  “Yes” if the organization reported on Part IX, line 3, column
                  (A), more than $5,000 of aggregate grants and other assistance
                  to foreign individuals, or to domestic organizations or
                  domestic individuals to provide grants or other assistance to
                  a designated foreign individual or individuals.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the organization selects “Yes,” it must complete Schedule
                  F, Parts III and IV.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 17</span> - Select
                  “Yes” to this line if the total amount reported for{' '}
                  <b>professional fundraising services</b> in Part IX (line 11e,
                  plus the portion of the line 6 amount attributable to
                  professional fundraising services) exceeds $15,000. If “Yes,”
                  the organization must complete Schedule G, Part I.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 18</span> - Select
                  “Yes” on line 18 if the sum of the amounts reported on lines
                  1c and 8a of Form 990, Part VIII, exceeds $15,000. If “Yes,”
                  the organization must complete Schedule G, Part II.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  An organization that answers “No” should consider whether to
                  complete Schedule G (Form 990) to report its fundraising
                  activities or gaming activities for state or other reporting
                  purposes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 19</span> - Select
                  “Yes” if the organization reported more than $15,000 of gross
                  income from gaming activities on Part VIII, line 9a. If “Yes,”
                  the organization must complete Schedule G, Part III.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part4-line20-21-img.webp" />
                  <source srcSet="/assets/images/f990-part4-line20-21-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part4-line20-21-img.png"
                    loading="lazy"
                    alt="Form 990 Part IV Lines 20–21 hospital & domestic grant reporting questions"
                    width={760}
                    height={66}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 20a</span> - Select
                  “Yes” if the organization, directly or indirectly through a
                  disregarded entity or joint venture treated as a partnership
                  for federal income tax purposes, operated one or more hospital
                  facilities at any time during the tax year. If selected “Yes,”
                  the organization must complete and attach Schedule H.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 20b</span> - If
                  answered “Yes” to line 20b, the organization must answer
                  whether it attached a copy of its most recent audited
                  financial statements to this return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 21</span> - Select
                  “Yes” if the organization reported on Part IX, line 1, column
                  (A), more than $5,000 of grants and other assistance to any
                  domestic organization, or any domestic government. If selected
                  “Yes,” the organization must complete Schedule I, Parts I and
                  II.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> Don&apos;t report grants or other assistance
                  provided to domestic organizations or domestic governments to
                  provide grants or other assistance to designated foreign
                  organizations or foreign individuals.
                </Alert>
                <picture>
                  <source srcSet="/assets/images/f990-part4-line22-24-img.webp" />
                  <source srcSet="/assets/images/f990-part4-line22-24-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part4-line22-24-img.png"
                    loading="lazy"
                    alt="Form 990 Part IV Lines 22–24 assistance, compensation & bond reporting questions"
                    width={760}
                    height={193}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 22</span> - Select
                  “Yes” if the organization reported on Part IX, line 2, column
                  (A), more than $5,000 of aggregate grants and other assistance
                  to or for domestic individuals. If selected “Yes,” the
                  organization must complete Schedule I, Parts I and III.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> Don&apos;t report grants or other assistance
                  provided to or for domestic individuals to provide grants or
                  other assistance to designated foreign organizations or
                  foreign individuals.
                </Alert>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>Line 23</span> - Select
                  “Yes” if the organization
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Listed in Part VII a former officer, director, trustee, key
                    employee, or highest compensated employee; or
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Reported for any person listed in Part VII more than
                    $150,000 of reportable compensation and other compensation.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If selected “Yes,” the organization must complete and attach
                  Schedule J.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 24a</span> - Select
                  “Yes” if the organization has a tax-exempt bond issue with an
                  outstanding principal amount of more than $100,000 as of the
                  last day of the year, that was issued after December 31, 2002.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you selected “Yes,” you must answer lines 24b through 24d
                  and complete Schedule K. If “No,” please proceed with line 25a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 24b</span> - Select
                  “Yes” if the organization invested any proceeds of tax-exempt
                  bonds beyond a temporary period exception.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  For purposes of line 24b, the organization need not include
                  the following as investments of proceeds.
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Any investment of proceeds relating to a reasonably required
                    reserve or replacement fund as described in section 148(d).
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Any investment of proceeds properly characterized as
                    replacement proceeds as defined in Regulations section
                    1.148-1(c).
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Any investment of net proceeds relating to a refunding
                    escrow as defined in Regulations section 1.148-1(b).
                    Temporary period exceptions are described in section 148(c)
                    and Regulations section 1.148-2(e)
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 24c</span> - Select
                  “Yes” if the organization maintains an escrow account other
                  than a refunding escrow at any time during the year to defease
                  any tax-exempt bonds.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  For purposes of line 24c, the organization is treated as
                  maintaining an escrow account if such account is maintained by
                  a trustee for tax-exempt bonds issued for the benefit of the
                  organization.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 24d</span> - Select
                  “Yes” if the organization acts as an “on behalf of” issuer for
                  bonds outstanding at any time during the year. Also, answer
                  “Yes” if the organization has outstanding qualified
                  scholarship funding bonds under section 150(d) or bonds of a
                  qualified volunteer fire department under section 150(e).
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part4-line25-27-img.webp" />
                  <source srcSet="/assets/images/f990-part4-line25-27-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part4-line25-27-img.png"
                    loading="lazy"
                    alt="Form 990 Part IV Lines 25–27 related-party & excess benefit transaction disclosures"
                    width={760}
                    height={196}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 25a-25b</span> -
                  Complete lines 25a and 25b only if the organization is a
                  section 501(c)(3), 501(c)(4), or 501(c)(29) organization. If
                  not, skip lines 25a and 25b and leave them blank.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 25a</span> - Select
                  “Yes” if the organization engaged in an excess benefit
                  transaction with a disqualified person during the year. If
                  selected “Yes,” the organization must complete <br />
                  <Link
                    href="/resources/tax-exempt/form-990-schedule-l/"
                    sx={{ color: '#0202ff' }}
                  >
                    Schedule L
                  </Link>
                  , Part I.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 25b</span> - Select
                  “Yes” if the organization became aware, before filing this
                  return, that it engaged in an excess benefit transaction with
                  a disqualified person in a prior year, and if the transaction
                  hasn&apos;t been reported on any of the organization&apos;s
                  prior Forms 990 or 990-EZ.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If selected “Yes,” the organization must complete Schedule L,
                  Part I.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 26</span> - Select
                  “Yes” if the organization reported any amount on Part X, line
                  5 or 22, for receivables from or payables to any current or
                  former officer, director, trustee, key employee, creator or
                  founder, substantial contributor, or 35% controlled entity or
                  family member of any of these persons.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If selected “Yes,” the organization must complete Schedule L,
                  Part II.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 27</span> - Select
                  “Yes” if the organization provided a grant or other assistance
                  to any current or former officer, director, trustee, key
                  employee, creator or founder, substantial contributor or
                  employee thereof, a grant selection committee member, or to a
                  35% controlled entity.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the organization selected “Yes,” it must complete Schedule
                  L, Part III.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part4-line28-34-img.webp" />
                  <source srcSet="/assets/images/f990-part4-line28-34-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part4-line28-34-img.png"
                    loading="lazy"
                    alt="Form 990 Part IV Lines 28–34 related-party, asset & entity relationship disclosures"
                    width={760}
                    height={279}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 28a</span> - Select
                  “Yes” if the organization was a party to a business
                  transaction with a current or former officer, director,
                  trustee, key employee, creator or founder, or substantial
                  contributor. If “Yes,” the organization must complete Schedule
                  L, Part IV.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 28b</span> - Select
                  “Yes” if the organization was a party to a business
                  transaction with a family member of any individual described
                  in line 28a. If “Yes,” the organization must complete Schedule
                  L, Part IV.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 28c</span> - Select
                  “Yes” if the organization was a party to a business
                  transaction with a 35% controlled entity of one or more
                  individuals and/or organizations described in lines 28a or
                  28b. If “Yes,” the organization must complete Schedule L, Part
                  IV.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 29</span> - Select
                  “Yes” if the organization received during the year more than
                  $25,000 in fair market value (FMV) of donations, gifts,
                  grants, or other contributions of property other than cash,
                  regardless of the manner received (such as for use in a
                  charity auction).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the organization selected “Yes,” it must complete Schedule
                  M.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> Don&apos;t include contributions of services or
                  use of facilities.
                </Alert>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>Line 30</span> - Select
                  “Yes” if the organization during the year received a donation,
                  gift, grant, or other contribution. It can be
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Any work of art, historical treasure, historical artifact,
                    scientific specimen, archaeological artifact, or similar
                    asset, including a fractional interest, regardless of the
                    amount or whether the organization maintains collections of
                    such items; or
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Any qualified conservation contributions regardless of
                    whether the contributor claimed a charitable contribution
                    deduction for such contribution.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the organization selected “Yes,” it must complete Schedule
                  M.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 31</span> - Select
                  “Yes” if the organization liquidated, terminated, dissolved,
                  ceased operations, or engaged in a significant disposition of
                  net assets during the year. If “Yes,” the organization must
                  complete Schedule N Part I.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 32</span> - Select
                  “Yes” if the organization sold, exchanged, disposed of, or
                  transferred more than 25% of its net assets. If “Yes,” the
                  organization must complete Schedule N Part II.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> Organizations that answered “Yes” on either of
                  the questions above must also check the box in Part I, line 2,
                  and complete Schedule N (Form 990), Part I, or Part II.
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 33</span> - Select
                  “Yes” if the organization owns 100% of an entity disregarded
                  as separate from the organization under Regulations sections
                  301.7701-2 and 301.7701-3. If “Yes,” the organization must
                  complete Schedule R Part I.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 34</span> - Select
                  “Yes” if the organization is related to any tax-exempt or
                  taxable entity. If “Yes,” the organization must complete
                  Schedule R Part II, III, or IV, and Part V, line 1.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> An organization that answers “Yes” on line 33
                  or 34 must enter its disregarded entities and related
                  organizations on Schedule R (Form 990) and provide specified
                  information regarding such organizations.
                </Alert>
                <picture>
                  <source srcSet="/assets/images/f990-part4-line35-38-img.webp" />
                  <source srcSet="/assets/images/f990-part4-line35-38-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part4-line35-38-img.png"
                    loading="lazy"
                    alt="Form 990 Part IV, Lines 35–38 covering controlled entities, related organization transactions, partnership activities & Schedule O disclosures"
                    width={760}
                    height={145}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 35a</span> - Select
                  “Yes” if the organization was a controlled entity of the
                  filing organization under section 512(b)(13) during the tax
                  year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 35b</span> - Select
                  “Yes” if the organization received any payment from or engaged
                  in any transaction with a controlled entity within the meaning
                  of section 512(b)(13). If “Yes,” the organization must
                  complete Schedule R, Part V, line 2.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 36</span> - Select
                  “Yes” only if the organization is a section 501(c)(3)
                  organization and engaged in a transaction over $50,000 during
                  the tax year with a related organization that was tax-exempt
                  under a section other than section 501(c)(3).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If “Yes,” the organization must complete Schedule R, Part V,
                  line 2.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 37</span> - Select
                  “Yes” if, at any time during the year, the organization
                  conducted more than 5% of its activities, measured by total
                  gross revenue for the tax year or total assets of the
                  organization at the end of its tax year, whichever is greater.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If “Yes,” the organization must complete Schedule R, Part V,
                  line 2.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 38</span> - Select
                  “Yes” if the organization completed Schedule O and provided
                  explanations on Schedule O for Part VI, lines 11b and 19.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> Schedule O (Form 990) must be completed and
                  filed by all organizations that file Form 990. All filers must
                  provide narrative responses to certain questions (for example,
                  Part VI, lines 11b and 19) on Schedule O (Form 990)
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part V - Statements Regarding Other IRS Filings and Tax
                  Compliance
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part5-line1-1c-img.webp" />
                  <source srcSet="/assets/images/f990-part5-line1-1c-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part5-line1-1c-img.png"
                    loading="lazy"
                    alt="Form 990-part-V (Statements Regarding Other IRS Filings and Tax Compliance ),Line 1a-1c"
                    width={760}
                    height={115}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1a</span> - This line
                  requires information from <b>Form 1096</b>, Annual Summary and
                  Transmittal of U.S. Information Returns, to transmit to the
                  IRS paper Forms 1099, 1098, 5498, and W-2G, which are
                  information returns reporting certain amounts paid or received
                  by the organization.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In this line, report all such returns filed for the calendar
                  year ending with or within the organization&apos;s tax year.
                  If not applicable, enter -0-.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1b</span> - In this
                  line, enter the number of Forms W-2G included on line 1a. If
                  not applicable, enter -0-
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1c</span> - Select
                  “Yes” if the organization complies with backup withholding
                  rules for reportable payments to vendors and reportable gaming
                  (gambling) winnings to prize winners.
                </Typography>
                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  For more information on backup withholding for missing or
                  incorrect names or taxpayer-identification numbers, see Pub.
                  1281, Backup Withholding for Missing and Incorrect
                  Name/TIN(s). I
                </Alert>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> If backup withholding rules didn&apos;t apply
                  to the organization because it didn&apos;t make a reportable
                  payment to a vendor or provide reportable gaming (gambling)
                  winnings to a prize winner, then leave line 1c blank
                </Alert>
                <picture>
                  <source srcSet="/assets/images/f990-part5-line2-5c-img.webp" />
                  <source srcSet="/assets/images/f990-part5-line2-5c-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part5-line2-5c-img.png"
                    loading="lazy"
                    alt="Form 990 Part V Lines 2a–5c employee, business, and foreign account reporting"
                    width={760}
                    height={197}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 2a</span> - In this
                  line, enter the number of the organization&apos;s employees
                  (not the number of Forms W-2) reported on a Form W-3, by both
                  the filing organization and reporting agents of the filing
                  organization, including common paymasters and payroll agents,
                  for the calendar year ending with or within the filing
                  organization&apos;s tax year. If not applicable, enter -0-.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 2b</span> - Select
                  “Yes” if the organization reported at least one employee on
                  line 2a.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If selected yes, answer whether the organization or reporting
                  agents of the organization filed all required federal
                  employment tax returns (which include Form 940,
                  Employer&apos;s Annual Federal Unemployment (FUTA) Tax Return;
                  and Form 941, Employer&apos;s QUARTERLY Federal Tax Return)
                  relating to such employees.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 3a</span> - Select
                  “Yes” if the organization&apos;s total gross income from
                  unrelated trades or businesses is $1,000 or more for the tax
                  year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 3b</span> - If the
                  organization selected Yes for line 3a, has it filed a Form
                  990-T for this year? If not, select “No” for line 3b, and
                  explain Schedule O (Form 990).
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>Line 4a</span> - Select
                  “Yes” to line 4a if either (1) or (2) below applies.
                </Typography>
                <ol style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 16 }}>
                    At any time during the calendar year ending with or within
                    the organization&apos;s tax year, the organization had an
                    interest in, or signature or other authority over, a
                    financial account in a foreign country (such as a bank
                    account, securities account, or other financial account);
                    and
                  </li>
                  <ol style={{ listStyleType: 'lower-alpha' }}>
                    <li style={{ marginBottom: 12 }}>
                      The combined value of all such accounts was more than
                      $10,000 at any time during the calendar year; and
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      The accounts weren&apos;t with a U.S. military banking
                      facility operated by a U.S. financial institution.
                    </li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>
                    The organization owns more than 50% of the stock in any
                    corporation that would answer “Yes” to item 1 above.
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you have selected “Yes,” you are required to e-file{' '}
                  <b>FinCEN Form 114</b>, Report of Foreign Bank and Financial
                  Accounts (FBAR), with the Department of the Treasury using
                  FinCEN&apos;s BSA E-Filing System.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 4b</span> - If selected
                  Yes for line 4a, enter the name of each foreign country in
                  which a foreign account described on line 4a is located. Also,
                  use Schedule O (Form 990) if more space is needed for
                  additional explanation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 5a</span> - Select
                  “Yes” to this line if the organization was party to a
                  prohibited tax shelter transaction as described in section
                  4965(e) at any time during the organization&apos;s tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 5b</span> - Select
                  “Yes” if any taxable party notified the organization that it
                  was or is a party to a prohibited tax shelter transaction. If
                  not, you can select “No”
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 5c</span> - If the
                  organization selected “Yes” to line 5a or 5b, answer whether
                  it filed Form 8886-T.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  An organization that files Form 990 (other than a section 527
                  political organization) and that is a party to a prohibited
                  tax shelter transaction must file <b>Form 8886-T</b>,
                  <i>
                    Disclosure by Tax-Exempt Entity Regarding Prohibited Tax
                    Shelter Transaction
                  </i>
                  , and may also have to file <b>Form 4720</b>,{' '}
                  <i>
                    Return of Certain Excise Taxes Under Chapters 41 and 42 of
                    the Internal Revenue Code
                  </i>
                  , and pay an excise tax imposed by section 4965.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> A prohibited tax shelter transaction is any
                  listed transaction, within the meaning of section 6707A(c)(2),
                  and any prohibited reportable transaction.
                </Alert>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> A prohibited reportable transaction is a
                  confidential transaction within the meaning of Regulations
                  section 1.6011-4(b)(3), and a transaction with contractual
                  protection within the meaning of Regulations section
                  1.6011-4(b) (4)
                </Alert>
                <picture>
                  <source srcSet="/assets/images/f990-part5-line6a-8-img.webp" />
                  <source srcSet="/assets/images/f990-part5-line6a-8-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part5-line6a-8-img.png"
                    loading="lazy"
                    alt="Form 990 Part V Lines 6a–8 fundraising and donor advised fund reporting"
                    width={760}
                    height={282}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 6a</span> - Select
                  “Yes” on this line only if the organization has annual gross
                  receipts that are normally greater than $100,000 and if it
                  solicited contributions not deductible under section 170
                  during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 6b</span> - If the
                  organization selected Yes to line 6a, select whether it
                  includes with every solicitation an express statement that
                  such contributions or gifts were not tax deductible.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> Failure to disclose that contributions
                  aren&apos;t deductible could result in a penalty of $1,000 for
                  each day on which a failure occurs. The maximum penalty for
                  failures by any organization, during any calendar year, shall
                  not exceed $10,000.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 7 - Organizations that may receive deductible
                  contributions under section 170(c).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 7a</span> - Select
                  “Yes” if the organization received a payment over $75 made
                  partly as a contribution and partly for goods and services
                  provided to the payor.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 7b</span> - If you
                  selected “Yes” to line 7b, answer whether you have notified
                  the donor of the value of goods and services provided.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Example - A donor gives a charity $100 in consideration for a
                  concert ticket valued at $40 (a quid pro quo contribution).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In this example, $60 would be deductible. Because the
                  donor&apos;s payment exceeds $75, the organization must
                  furnish a disclosure statement even though the taxpayer&apos;s
                  deductible amount doesn&apos;t exceed $75. Separate payments
                  of $75 or less made at different times of the year for
                  separate fundraising events won&apos;t be aggregated for
                  purposes of the $75 threshold
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 7c</span> - Select
                  “Yes” if the organization sold, exchanged, or otherwise
                  disposed of tangible personal property for which it was
                  required to file Form 8282.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 7d</span> - If you have
                  selected Yes on line 7c, indicate the number of{' '}
                  <b>Forms 8282</b>, <i>Donee Information Return</i>, filed
                  during the year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 7e</span> - Select
                  “Yes” on this line if the organization received any funds,
                  directly or indirectly, to pay premiums on a personal benefit
                  contract.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 7f</span> - Select
                  “Yes” on line 7f if the organization, during the tax year,
                  paid premiums, directly or indirectly, on a personal benefit
                  contract.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note : </b>For lines 7e and 7f, the organization must file{' '}
                  <b>Form 8870</b>,{' '}
                  <i>
                    Information Return for Transfers Associated With Certain
                    Personal Benefit Contracts
                  </i>
                  , and report the premiums it paid, and the premiums paid by
                  others but treated as paid by the organization. The
                  organization must report and pay an excise tax, equal to
                  premiums paid, on Form 4720.
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 7g</span> - Select
                  “Yes” if the organization received a contribution of qualified
                  intellectual property*, and filed <b>Form 8899</b>, Notice of
                  Income From Donated Intellectual Property as required.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> <b>Qualified intellectual property</b> is any
                  patent, copyright (other than certain self-created
                  copyrights), trademark, trade name, trade secret, know-how,
                  software (other than certain “canned” or “off-the-shelf”
                  software or self-created software), or similar property, or
                  applications or registrations of such property.
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 7h</span> - Select
                  “Yes” if the organization received a contribution of cars,
                  boats, airplanes, or other vehicles, and filed Form 1098-C,
                  Contributions of Motor Vehicles, Boats, and Airplanes as
                  required.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 8 - Sponsoring organizations maintaining donor-advised
                  funds.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select “Yes” on line 8 if your organization is a sponsoring
                  organization of a donor-advised fund with any one of its
                  donor-advised funds having excess business holdings at any
                  time during the organization&apos;s tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If your organization has selected “Yes,” see the instructions
                  for <b>Schedule C</b> of Form 4720 to determine whether it is
                  subject to the excess business holdings tax under section 4943
                  and is required to file Form 4720.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 9 - Sponsoring organizations maintaining donor-advised
                  funds.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part5-line9-13c-img.webp" />
                  <source srcSet="/assets/images/f990-part5-line9-13c-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part5-line9-13c-img.png"
                    loading="lazy"
                    alt="Form 990 Part V Lines 9–13c DAFs, clubs, charitable trusts, and health insurance issuer reporting"
                    width={760}
                    height={293}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 9a</span> - Select “
                  <b>Yes</b>” on line 9a if the sponsoring organization made any
                  taxable distributions under section 4966 during the
                  organization&apos;s tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you have selected “Yes,” the organization must complete and
                  file Form 4720, Schedule K, to calculate and pay the tax.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 9b</span> - Select
                  “Yes” to this line if the organization made a distribution
                  from a donor advised fund to a donor, donor advisor, or
                  related person during the organization&apos;s tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you have selected “Yes,” the organization must complete and
                  file Form 4720, Schedule L (Form 990).
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> A related person is any family member of the
                  donor or donor advisor and any 35% controlled entity (as
                  defined in section 4958(f)) of the donor or donor advisor.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 10 - Section 501(c)(7) organizations
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Here answer lines 10a and 10b only if your organization is
                  exempt under section 501(c)(7).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 10a</span> - Enter in
                  this line the amount of initiation fees, capital
                  contributions, and unusual amounts of income included in Part
                  VIII. Statement of Revenue, line 12, Total revenue, but not
                  included in the definition of gross receipts for section
                  501(c)(7) exemption purposes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  However, if the organization is a college fraternity or
                  sorority that charges membership initiation fees but not
                  annual dues, don&apos;t include such initiation fees.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 10b</span> - In line
                  10b, enter the amount of gross receipts included in Part VIII.
                  Statement of Revenue, line 12, Total Revenue, derived from the
                  general public for use of the organization&apos;s facilities,
                  that is, from persons other than members or their spouses,
                  dependents, or guests.
                </Typography>
                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  Include the amount entered on line 10b of Form 990 on the
                  club&apos;s Form 990-T if required to be filed.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 11 - Section 501(c)(12) organizations
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Here answer lines 11a and 11b only if the organization is
                  exempt under section 501(c)(12).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 11a</span> - In this
                  line, enter the gross income received from members or
                  shareholders.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 11b</span> - In this
                  line, enter the gross income received from other sources.
                  However, do not include in this line the net amounts due or
                  paid to other sources against amounts due or received from
                  them.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> One of the requirements that an organization
                  must meet to qualify under section 501(c)(12) is that at least
                  85% of its gross income consists of amounts collected from
                  members for the sole purpose of meeting losses and expenses.
                  For purposes of section 501(c)(12), the term “gross income”
                  means gross receipts without reduction for any cost of goods
                  sold.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 12 - Section 4947(a)(1) non-exempt charitable trusts.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your organization doesn’t come under section 4947(a)(1)
                  trusts, you are to leave line 12 blank.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 12a</span> - Select
                  “Yes” if the organization is filing Form 990 instead of Form
                  1041.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 12b</span> - If you
                  have selected Yes for line 12b, enter the amount of tax-exempt
                  interest received or accrued during the year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 13 - Section 501(c)(29) qualified nonprofit health
                  insurance issuers.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Here answer lines 13a, 13b, and 13c only if the organization
                  has received a loan or grant under the Department of Health
                  and Human Services CO-OP program.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 13a</span> - Select
                  “Yes” if the organization is licensed to issue qualified
                  health plans in more than one state. If not, select “No”.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In either case, report on Schedule O (Form 990) each state in
                  which the organization is licensed to issue qualified health
                  plans, the dollar amount of reserves each state requires the
                  organization to maintain, and the dollar amount of reserves
                  the organization maintains and reports to each state.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 13b</span> - Enter this
                  line the highest dollar amount of reserves the organization is
                  required to maintain by any of the states in which the
                  organization is licensed to issue qualified health plans.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 13c</span> - Enter in
                  line 13c the highest dollar amount of reserves the
                  organization maintains on hand and report to a state in which
                  the organization is licensed to issue qualified health plans.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part5-line14-17-img.webp" />
                  <source srcSet="/assets/images/f990-part5-line14-17-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part5-line14-17-img.png"
                    loading="lazy"
                    alt="Form 990 Part V Lines 14–17 excise tax and reporting questions"
                    width={760}
                    height={169}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 14a</span> - Select
                  “Yes” if the organization received any payments during the
                  year for indoor tanning services.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> Indoor tanning services” are services employing
                  any electronic product designed to incorporate one or more
                  ultraviolet lamps and intended for the irradiation of an
                  individual by ultraviolet radiation, with wavelengths in air
                  between 200 and 400 nanometers, to induce skin tanning.
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 14b</span> - If the
                  organization selected Yes on line 14a, mention whether it
                  filed a Form 720 to report those payments. If “No,” explain in
                  Schedule O (Form 990) why it didn&apos;t file Form 720.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 15</span> - Select
                  “Yes” if the organization was subject to the section 4960 tax
                  on payment(s) of more than $1,000,000 in remuneration or
                  excess parachute payment(s) during the year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you have selected Yes, file Form 4720, Schedule N to
                  determine if you paid any covered employee more than $1
                  million in remuneration or paid an excess parachute payment
                  during the year. Remuneration paid to a covered employee
                  includes any remuneration paid by a related organization.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 16
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Line 16 applies to private colleges and universities subject
                  to the excise tax on net investment income under section 4968.
                  All other organizations, including state colleges and
                  universities described in the first sentence of section
                  511(a)(2) (B), are not subject to this tax, and therefore
                  check the “No” box on line 16, and go to Part VI.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Select “Yes” if the organization is an educational institution
                  subject to the section 4968 excise tax on net investment
                  income. If you have selected “Yes,” complete Form 4720,
                  Schedule O.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 17 - Section 501(c)(21) organizations
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select “Yes” if the trust, or any disqualified or other person
                  engaged in any activities that would result in the imposition
                  of an excise tax under sections 4951, 4952, or 4953.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you have selected “Yes,” the organization must complete
                  Form 6069.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part VI - Governance, Management, and Disclosure
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part6-line1a-6-img.webp" />
                  <source srcSet="/assets/images/f990-part6-line1a-6-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part6-line1a-6-img.png"
                    loading="lazy"
                    alt="Form 990 Part VI Lines 1a–6 governance structure and key management oversight"
                    width={760}
                    height={279}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Section A. Governing Body and Management
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1a</span> - Enter the
                  number of governing body members of the organization with the
                  power to vote on all matters that come before the governing
                  body (other than when a conflict of interest disqualifies the
                  member from voting)
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> The governing body is a group of one or more
                  persons authorized under state law to make governance
                  decisions on behalf of the organization and its shareholders
                  or members, if applicable
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If members of the governing body don&apos;t all have the same
                  voting rights, the organization must explain the material
                  differences on Schedule O (Form 990).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1b</span> - Enter the
                  number of independent voting members of the governing body as
                  of the end of the organization&apos;s tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 2</span> - Select “Yes”
                  if any of the organization&apos;s current officers, directors,
                  trustees, or key employees, as reported in Part VII, Section
                  A, had a family relationship or business relationship with
                  another of the organization&apos;s current officers,
                  directors, trustees, or key employees, at any time during the
                  organization&apos;s tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the organization has any such relationship, you must
                  identify the persons for each family and business
                  relationship, and describe their relationship on Schedule O
                  (Form 990).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 3</span> - Select “Yes”
                  if, at any time during the organization&apos;s tax year, the
                  organization used a management company or other person (other
                  than persons acting in their capacities as officers,
                  directors, trustees, or key employees) to perform any
                  management duties customarily performed by or under the direct
                  supervision of officers, directors, trustees, or key
                  employees.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  They can be duties that include but aren&apos;t limited to,
                  hiring, firing, and supervising personnel; planning or
                  executing budgets or financial operations; or supervising
                  exempt operations or unrelated trades or businesses of the
                  organization.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If selected “Yes” for line 3, provide the following details in
                  Schedule O.
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Name(s) of the management company or companies or other
                    person(s) performing management duties
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    services they provided to the organization
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    List of the organization’s current or former officers,
                    directors, trustees, key employees, and highest compensated
                    employees listed in Part VII, Section A, who were
                    compensated by the management company or companies or other
                    person(s) during the calendar year ending with or within the
                    organization&apos;s tax year
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    List the amounts of reportable and other compensation they
                    received from the management company or companies or other
                    person(s) for services provided to the filing organization
                    and related organizations during that year.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 4</span> - Select “Yes”
                  if the organization has made any significant changes to its
                  governing documents since the prior Form 990 was filed.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  However, don&apos;t report changes to policies described or
                  established outside of the organizing or enabling document and
                  bylaws (or similar documents), such as the adoption of, or
                  change to, a policy adopted by resolution of the governing
                  body that doesn&apos;t entail a change to the organizing
                  document or bylaws.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 5</span> - Select “Yes”
                  if the organization became aware during the year of a
                  significant diversion of the organization’s assets.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If selected “Yes,” the organization must explain the nature of
                  the diversion, dollar amounts and/or other property involved,
                  corrective actions taken to address the matter, and pertinent
                  circumstances on Schedule O (Form 990), although the person or
                  persons who diverted the assets shouldn&apos;t be identified
                  by name.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> A <i>diversion of assets</i> includes any
                  unauthorized conversion or use of the organization&apos;s
                  assets other than for the organization&apos;s authorized
                  purposes, including but not limited to embezzlement or theft.
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 6</span> - Select “Yes”
                  if the organization has members or stockholders and is
                  organized as a stock corporation, a joint-stock company, a
                  partnership, a joint venture, or an LLC.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part6-line7-9-img.webp" />
                  <source srcSet="/assets/images/f990-part6-line7-9-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part6-line7-9-img.png"
                    loading="lazy"
                    alt="Form 990 Part VI Lines 7–9 governance and official contact information"
                    width={760}
                    height={168}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 7a</span> - Select
                  “Yes” if, at any time during the organization&apos;s tax year,
                  there were one or more persons (other than the
                  organization&apos;s governing body itself, acting in such
                  capacity) that had the right to elect or appoint one or more
                  members of the organization&apos;s governing body, whether
                  periodically, or as vacancies arise, or otherwise
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you have selected “Yes,” the organization must explain on{' '}
                  <b>Schedule O</b> (Form 990) the class or classes of such
                  persons and the nature of their rights.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 7b</span> - Select
                  “Yes” if, at any time during the organization&apos;s tax year,
                  any governance decisions of the organization were reserved to
                  (or subject to approval by) members, stockholders, or persons
                  other than the governing body.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you have selected “Yes,” the organization must explain on{' '}
                  <b>Schedule O</b> (Form 990) the class or classes of such
                  persons, the decisions that require their approval, and the
                  nature of their voting rights.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 8a</span> - Select
                  “Yes” if the organization contemporaneously documented the
                  meetings held or written actions undertaken during the year by
                  the governing body.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> Documentation permitted by state law can
                  include approved minutes, emails, or similar writings that
                  explain the action taken, when it was taken, and who made the
                  decision.
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 8b</span> - Select
                  “Yes” if the organization contemporaneously documented the
                  meetings held or written actions undertaken during the year by
                  each committee with the authority to act on behalf of the
                  governing body.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 9</span> - Select “Yes”
                  if there was any officer, director, trustee, or key employee
                  listed in Part VII, Section A, who cannot be reached at the
                  organization’s mailing address.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If selected “Yes,” enter on Schedule O (Form 990) the mailing
                  addresses for such persons who are to be contacted at a
                  different address.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Section B. Policies
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part6-line10-14-img.webp" />
                  <source srcSet="/assets/images/f990-part6-line10-14-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part6-line10-14-img.png"
                    loading="lazy"
                    alt="Form 990 Part VI Lines 10–14 governance and compliance policies"
                    width={760}
                    height={214}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 10a</span> - Select
                  “Yes” if the organization during the tax year had any local
                  chapters, local branches, local lodges, or other similar local
                  units or affiliates over which the organization had the legal
                  authority to exercise direct or indirect supervision and
                  control (whether or not in a group exemption)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 10b</span> - If you
                  have selected “Yes” to line 10a, answer whether the
                  organization has written policies and procedures governing the
                  activities of such chapters, affiliates, and branches to
                  ensure their operations are consistent with the organization’s
                  exempt purposes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If selected “No,” explain on Schedule O (Form 990) how the
                  organization ensures that the local unit&apos;s activities are
                  consistent with its tax-exempt purposes.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> The central organization (parent organization)
                  named in a group exemption letter is required to have general
                  supervision or control over its subordinate organizations as a
                  condition of the group exemption.
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 11a</span> - Select
                  “Yes” only if a complete copy of the organization&apos;s final
                  Form 990 (including all required schedules), as ultimately
                  filed with the IRS, was provided to each person who was a
                  voting member of the governing body at the time the Form 990
                  was provided, whether in paper or electronic form, before its
                  filing with the IRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  However, you must select “No” if the organization merely
                  informed its governing body members that a copy of the Form
                  990 is available upon request or redacted or removed any
                  information from the copy of its final Form 990 that it
                  provided to its governing body members before filing the form.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 11b</span> - To
                  complete this line, describe on Schedule O (Form 990) the
                  process, if any, by which any of the organization&apos;s
                  officers, directors, trustees, board committee members, or
                  management reviewed the prepared Form 990, whether before or
                  after it was filed with the IRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 12a</span> - Select
                  “Yes” if, as of the end of the organization&apos;s tax year,
                  the organization had a written conflict of interest policy.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you have selected “No,” you can skip and go to line 13.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> A <b>conflict of interest policy</b> defines
                  conflicts of interest, identifies the classes of individuals
                  within the organization covered by the policy, facilitates
                  disclosure of information that can help identify conflicts of
                  interest, and specifies procedures to be followed in managing
                  conflicts of interest
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 12b</span> - Select
                  “Yes” if the organization&apos;s officers, directors,
                  trustees, and key employees are required to disclose or update
                  annually (or more frequently) the information regarding their
                  interests and those of their family members that could give
                  rise to conflicts of interest.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 12c</span> - Select
                  “Yes” if the organization regularly and consistently monitors
                  and enforces compliance with the policy. If selected Yes, the
                  organization must explain on Schedule O its practices for
                  monitoring proposed or ongoing transactions for conflicts of
                  interest and dealing with potential or actual conflicts,
                  whether discovered before or after the transaction has
                  occurred.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The description should include an explanation of which persons
                  are covered under the policy, the level at which
                  determinations of whether a conflict exists are made, and the
                  level at which actual conflicts are reviewed. Also explain any
                  restrictions imposed on persons with a conflict, such as
                  prohibiting them from participating in the governing
                  body&apos;s deliberations and decisions in the transaction.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 13</span> - Select
                  “Yes” if the organization has written whistleblower policy.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> A <b>whistleblower policy</b> encourages staff
                  and volunteers to come forward with credible information on
                  illegal practices or violations of adopted policies of the
                  organization. It also specifies that the organization will
                  protect the individual from retaliation, and identifies those
                  staff or board members or outside parties to whom such
                  information can be reported.
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 14</span> - Select
                  “Yes” if the organization has written a document retention and
                  destruction policy.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> A{' '}
                  <b>document retention and destruction policy</b> identifies
                  the record retention responsibilities of staff, volunteers,
                  board members, and outsiders for maintaining and documenting
                  the storage and destruction of the organization&apos;s
                  documents and records.
                </Alert>
                <picture>
                  <source srcSet="/assets/images/f990-part6-line15-16-img.webp" />
                  <source srcSet="/assets/images/f990-part6-line15-16-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part6-line15-16-img.png"
                    loading="lazy"
                    alt="Form 990 Part VI Lines 15–16 compensation review and joint venture policies"
                    width={760}
                    height={164}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 15a</span> - Select
                  “Yes” on line 15a if, during the tax year, the organization
                  included a review and approval process by independent persons,
                  comparability data, and contemporaneous substantiation of the
                  deliberation and decision for the process for determining
                  compensation of the organization’s CEO, Executive Director, or
                  top management official.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 15b</span> - Select
                  “Yes” on line 15a if, during the tax year, the organization
                  included a review and approval process by independent persons,
                  comparability data, and contemporaneous substantiation of the
                  deliberation and decision for the process for determining
                  compensation of other officers or key employees of the
                  organization.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you selected “Yes” on line 15a or 15b, describe the process
                  on <b>Schedule O</b> (Form 990), identify the offices or
                  positions for which the process was used to establish the
                  compensation of the persons who served in those offices or
                  positions, and enter the year in which this process was last
                  undertaken for each such person.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 16a</span> - Select
                  “Yes” if at any time during its tax year, the organization
                  invested in, contributed assets to, or otherwise participated
                  in a joint venture or similar arrangement with one or more
                  taxable persons.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 16b</span> - If you
                  have selected “Yes” on line 16a, answer whether or not the
                  organization followed a written policy or procedure requiring
                  the organization to evaluate its participation in joint
                  venture arrangements under applicable federal tax law, and
                  take steps to safeguard the organization’s exempt status to
                  such arrangements.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Section C. Disclosure
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part6-line17-20-img.webp" />
                  <source srcSet="/assets/images/f990-part6-line17-20-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part6-line17-20-img.png"
                    loading="lazy"
                    alt="Form 990 Part VI Lines 17–20 public disclosure & recordkeeping"
                    width={760}
                    height={145}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 17</span> - In line 17,
                  list the states with which a copy of this Form 990 is required
                  to be filed, even if the organization hasn&apos;t yet filed
                  Form 990 with that state. You can use Schedule O (Form 990) if
                  additional space is necessary.
                </Typography>
                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  Some states require or permit the filing of Form 990 to
                  fulfill state-exempt organization or charitable solicitation
                  reporting requirements.
                </Alert>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>Line 18</span> - To complete
                  this line, indicate how the organization made Forms 1023 (1024
                  or 1024-A, if applicable), 990, and 990-T available for public
                  inspection.
                </Typography>
                <ol style={{ marginBottom: 16 }}>
                  <li style={{ marginBottom: 12 }}>Own website</li>
                  <li style={{ marginBottom: 12 }}>Another&apos;s website</li>
                  <li style={{ marginBottom: 12 }}>Upon request</li>
                  <li style={{ marginBottom: 12 }}>
                    Other (explain on Schedule O)
                  </li>
                </ol>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> A Organizations that file Form 990 must make it
                  publicly available for 3 years from the date it is required to
                  be filed (including extensions) or, if later, is filed.
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 19</span> - To complete
                  this line, the organization must explain on <b>Schedule O</b>{' '}
                  whether it made its governing documents (for example, articles
                  of incorporation, constitution, bylaws, trust instrument),
                  conflict of interest policy, and financial statements (whether
                  or not audited) available to the general public during the tax
                  year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Also, it should mention how it made them available to the
                  public (for example, posting on the organization&apos;s
                  website, posting on another website, providing copies on
                  request, inspection at an office of the organization, etc.).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the filing organization didn&apos;t make any of these
                  documents available to the public, enter “No documents
                  available to the public.” on line 19.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 20</span> - Provide the
                  name of the person who possesses the organization&apos;s books
                  and records, and the business address and telephone number of
                  such person (or of the organization if the books and records
                  are kept by such person at a personal residence).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the books and records are kept at more than one location,
                  provide the name, business address, and telephone number of
                  the person responsible for coordinating the maintenance of the
                  books and records.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part VII - Compensation of Officers, Directors, Trustees, Key
                  Employees, Highest Compensated Employees, and Independent
                  Contractors
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part7-line1a-img.webp" />
                  <source srcSet="/assets/images/f990-part7-line1a-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part7-line1a-img.png"
                    loading="lazy"
                    alt="Form 990 Part VII Section A Line 1a officer and key employee listing"
                    width={760}
                    height={299}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Form 990, Part VII, requires the listing of the
                  organization&apos;s current or former officers, directors,
                  trustees, key employees, highest compensated employees, and
                  current independent contractors, and the reporting of certain
                  compensation information relating to such persons.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Section A. Officers, Directors, Trustees, Key Employees, and
                  Highest Compensated Employees
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part7-section1a-img.webp" />
                  <source srcSet="/assets/images/f990-part7-section1a-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part7-section1a-img.png"
                    loading="lazy"
                    alt="Form 990 Part VII Section A Line 1a Columns A–F officer and key employee compensation"
                    width={760}
                    height={627}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1a</span> - Complete
                  this table for all persons required to be listed. Report
                  compensation for the calendar year ending with or within the
                  organization’s tax year.
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Column (A) - Name and title
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Column (B) - Average hours per week
                  </li>
                  <li style={{ marginBottom: 12 }}>Column (C) - Position</li>
                  <ol style={{ marginBottom: 12, listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 12 }}>Former</li>
                    <li style={{ marginBottom: 12 }}>
                      Highest Compensated Employee
                    </li>
                    <li style={{ marginBottom: 12 }}>Key Employee</li>
                    <li style={{ marginBottom: 12 }}>Officer</li>
                    <li style={{ marginBottom: 12 }}>Institutional Trustee</li>
                    <li style={{ marginBottom: 12 }}>
                      Individual trustee or director
                    </li>
                  </ol>
                  <li style={{ marginBottom: 12 }}>
                    Column (D) - Reportable compensation from the organization (
                    <Link
                      href="/resources/information-returns/form-w-2-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      W-2
                    </Link>
                    /{' '}
                    <Link
                      href="/resources/information-returns/form-1099-misc-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      1099-MISC
                    </Link>
                    /<br />{' '}
                    <Link
                      href="/resources/information-returns/form-1099-nec-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      1099-NEC
                    </Link>
                    )
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Column (E) - Reportable compensation from related
                    organization (W-2/ 1099-MISC/
                    <br /> 1099-NEC)
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Column (F) - Estimated amount of other compensation from the
                    organization and related organizations.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  While completing Part VII, make sure to list the following
                  individuals.
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    List all of the organization’s current officers, directors,
                    and trustees (whether individuals or organizations),
                    regardless of the amount of compensation. Enter -0- in
                    columns (D), (E), and (F) if no compensation was paid.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    List all of the organization’s current key employees, if
                    any. See the instructions for the definition of “key
                    employee.”
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    List the organization’s five current highest compensated
                    employees (other than an officer, director, trustee, or key
                    employee) who received reportable compensation (box 5 of
                    Form W-2, box 6 of Form 1099-MISC, and/or box 1 of Form
                    1099-NEC) of more than $100,000 from the organization and
                    any related organizations.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    List all of the organization’s former officers, key
                    employees, and highest compensated employees who received
                    more than $100,000 of reportable compensation from the
                    organization and any related organizations.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    List all of the organization’s former directors or trustees
                    who received, in the capacity as a former director or
                    trustee of the organization, more than $10,000 of reportable
                    compensation from the organization and any related
                    organizations.
                  </li>
                </ul>
                <picture>
                  <source srcSet="/assets/images/f990-part7-line1b-1d-img.webp" />
                  <source srcSet="/assets/images/f990-part7-line1b-1d-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part7-line1b-1d-img.png"
                    loading="lazy"
                    alt="Form 990 Part VII Lines 1b–1d subtotal and total compensation "
                    width={760}
                    height={49}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.4,
                  }}
                >
                  Line 1b – Subtotal
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    subtotal of compensation
                  </span>{' '}
                  for the individuals listed in{' '}
                  <span style={{ fontWeight: 600 }}>Line 1a.</span>
                  Add the amounts shown in{' '}
                  <span style={{ fontWeight: 600 }}>
                    columns (D), (E), and (F)
                  </span>{' '}
                  for everyone listed in the main table, and enter each column’s
                  total on Line 1b.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.4,
                  }}
                >
                  Line 1c – Subtotal from Continuation Tables
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Complete this line{' '}
                  <span style={{ fontWeight: 600 }}>
                    only if you listed more than 25 individuals
                  </span>
                  and used continuation (duplicate) Section A tables.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Add the compensation amounts from those tables in{' '}
                  <span style={{ fontWeight: 600 }}>
                    columns (D), (E), and (F)
                  </span>{' '}
                  and enter the subtotals here.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If no continuation tables were used, enter{' '}
                  <span style={{ fontWeight: 600 }}>$0.</span>
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.4,
                  }}
                >
                  Line 1d – Total Compensation{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Add{' '}
                  <span style={{ fontWeight: 600 }}>Line 1b and Line 1c</span>{' '}
                  for each column—
                  <span style={{ fontWeight: 600 }}>(D), (E), and (F)</span>—and
                  enter the final totals on Line 1d. and used continuation
                  (duplicate) Section A tables.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This line represents the{' '}
                  <span style={{ fontWeight: 600 }}>
                    total compensation reported in Part VII, Section A.
                  </span>
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part7-line2-5-img.webp" />
                  <source srcSet="/assets/images/f990-part7-line2-5-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part7-line2-5-img.png"
                    loading="lazy"
                    alt="Form 990 Part VII Lines 2–5 high-compensation reporting and Schedule J requirements"
                    width={760}
                    height={164}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 2</span> - In this
                  line, report the total number of individuals, both those
                  listed in Part VII, Section A, table, and those not listed, to
                  whom the filing organization (not related organizations) paid
                  over $100,000 in reportable compensation during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 3</span> - Select “Yes”
                  if the organization listed any former officer, director,
                  trustee, key employee, or highest compensated employee on line
                  1a. If you have selected “Yes,” the organization must complete
                  and attach Schedule J for such individuals.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 4</span> - Select “Yes”
                  if for any individual listed on line 1a, is the sum of
                  reportable compensation and other compensation from the
                  organization and related organizations greater than $150,000.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you have selected “Yes,” the organization must complete and
                  attach Schedule J for such individuals.
                </Typography>
                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  To determine whether any listed individual received or accrued
                  more than $150,000 of reportable and other compensation, add
                  all compensation included in Part VII, Section A, columns (D),
                  (E), and (F), but disregard any decreases in the actuarial
                  value of defined benefit plans.
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 5</span> - Select “Yes”
                  if any person listed on line 1a received or accrued
                  compensation from any unrelated organization or individual for
                  services rendered to the organization.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If selected “Yes,” the organization must complete and attach
                  Schedule J for such persons. Also, specify in Part III, the
                  name of the unrelated organization, the type and amount of
                  compensation it paid or accrued, and the person receiving or
                  accruing such compensation.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Section B. Independent Contractors
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part7-sectionb-img.webp" />
                  <source srcSet="/assets/images/f990-part7-sectionb-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part7-sectionb-img.png"
                    loading="lazy"
                    alt="Form 990 Part VII Section B Lines 1–2 independent contractor compensation"
                    width={760}
                    height={197}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1</span> - To complete
                  this line, provide the details of the five highest compensated
                  independent contractors that received more than $100,000 in
                  compensation for services, whether professional or other
                  services, from the organization.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The details include
                </Typography>
                <ol style={{ marginBottom: 16 }}>
                  <li style={{ marginBottom: 12 }}>
                    Name and business address
                  </li>
                  <li style={{ marginBottom: 12 }}>Description of services</li>
                  <li style={{ marginBottom: 12 }}>Compensation</li>
                </ol>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> Don&apos;t report public utilities or insurance
                  providers as independent contractors.
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Line 2</span> - In line 2,
                  enter the total number of independent contractors (including
                  but not limited to those listed above) who received more than
                  $100,000 of compensation from the organization.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part VIII - Statement of Revenue
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part8-line1a-img.webp" />
                  <source srcSet="/assets/images/f990-part8-line1a-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part8-line1a-img.png"
                    loading="lazy"
                    alt="Form 990 Part VIII, Lines 1a–1h showing contributions, gifts, grants, and other similar amounts"
                    width={760}
                    height={246}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Contributions, Gifts, Grants, and Other Similar Amounts
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1a</span> - In this
                  line, enter the total amount of contributions received
                  indirectly from the public through solicitation campaigns
                  conducted by federated fundraising agencies and similar
                  fundraising organizations.
                </Typography>
                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  Federated fundraising agencies must, like all other filers,
                  identify the sources of contributions made to them on lines 1a
                  through 1g.
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1b</span> - Report on
                  line 1b the membership dues and assessments that represent
                  contributions from the public rather than payments for
                  benefits received or payments from affiliated organizations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1c</span> -In this
                  line, enter the total amount of contributions received from
                  fundraising events, including dinners, auctions, and other
                  events conducted for the sole or primary purpose of raising
                  funds for the organization&apos;s exempt activities.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you have contributions received from gaming activities,
                  report those contributions on line 1f, not on line 1c.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1d</span> - Enter here
                  the amounts contributed to the organization by related
                  organizations. Don&apos;t report amounts reportable on line
                  1a.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1e</span> - Enter the
                  total amount of contributions in the form of grants or similar
                  payments from local, state, or federal government sources, as
                  well as foreign governments. While reporting contributions,
                  include grant amounts from U.S. territories.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1f</span> - Enter on
                  line 1f all other contributions, gifts, and similar amounts
                  the organization received from sources not reported separately
                  on lines 1a through 1e.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This amount includes contributions from donor advised funds
                  (unless the sponsoring organization is a related organization)
                  and from gaming activities.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1g</span> - Enter on
                  this line the value of noncash contributions included on lines
                  1a through 1f. If this amount exceeds $25,000, the
                  organization must select “Yes” on Part IV, line 29, and
                  complete and attach Schedule M (Form 990).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Noncash contributions are anything other than cash, checks,
                  money orders, credit card charges, wire transfers, and other
                  transfers and deposits to a cash account of the organization.
                </Typography>
                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  Museums and other organizations that elect not to capitalize
                  their collections (according to ASC 958-360-45) shouldn&apos;t
                  report an amount on line 1g for works of art and other
                  collection items donated to them.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Program Service Revenue
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part8-line2a-img.webp" />
                  <source srcSet="/assets/images/f990-part8-line2a-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part8-line2a-img.png"
                    loading="lazy"
                    alt="Form 990 Part VIII, Lines 2a–2g showing program service revenue"
                    width={760}
                    height={131}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 2a - 2e</span> - On
                  lines 2a through 2e, enter the organization&apos;s five
                  largest sources of program service revenue.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 2f</span> - On line
                  2f, enter the total received from all other sources of program
                  service revenue not listed individually on lines 2a through
                  2e.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> Program service revenue includes income earned
                  by the organization for providing a government agency with a
                  service, facility, or product that benefited that government
                  agency directly rather than benefiting the public as a whole.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Other Revenue
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part8-line3-6d-img.webp" />
                  <source srcSet="/assets/images/f990-part8-line3-6d-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part8-line3-6d-img.png"
                    loading="lazy"
                    alt="Form 990 Part VIII Lines 3–6d investment, royalties & rental income"
                    width={760}
                    height={150}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 3</span> - Enter in
                  this line the gross amount of interest income from savings and
                  temporary cash investments, dividend and interest income from
                  equity and debt securities (stocks and bonds), and amounts
                  received from payments on securities loans, as defined in
                  section 512(a)(5), as well as interest from notes and loans
                  receivable.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  However, don&apos;t include unrealized gains and losses on
                  investments carried at FMV.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 4</span> - Enter in
                  this line all investment income actually or constructively
                  received from investing the proceeds of a tax-exempt bond
                  issue, which are under the control of the organization.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  However, don&apos;t include any investment income received
                  from investing proceeds that are technically under the control
                  of the governmental issuer.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 5</span> - Enter here
                  the royalties received by the organization from licensing the
                  ongoing use of its property to others. This includes payments
                  to the owner of the property for the right to exploit natural
                  resources on the property, such as oil, natural gas, or
                  minerals.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 6a - Gross Rents
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter on line 6a the rental income received for the year from
                  investment property and any other real property rented by the
                  organization.
                </Typography>
                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  Rental revenue can be from an activity that is related or
                  unrelated to the organization&apos;s exempt purpose. In
                  general, rents from real property are excluded in computing
                  unrelated business income, while rental income from personal
                  property is included.
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 6b</span> - Enter on
                  this line the expenses paid or incurred for the income
                  reported on line 6a. Also include interest related to rental
                  property and depreciation if it is recorded in the
                  organization&apos;s books and records.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Lines 7a - 7d
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part8-line7a-10-img.webp" />
                  <source srcSet="/assets/images/f990-part8-line7a-10-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part8-line7a-10-img.png"
                    loading="lazy"
                    alt="Form 990 Part VIII Lines 7a–10 revenue from sales, fundraising, gaming & inventory"
                    width={760}
                    height={346}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter on lines 7a through 7c all sales of securities in Column
                  (i) and use Column (ii) to report sales of all other types of
                  investments (such as real estate, royalty interests, or
                  partnership interests) and all other non-inventory assets
                  (such as program-related investments and fixed assets used by
                  the organization in its related and unrelated activities).
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> Don&apos;t include any unrealized gains or
                  losses on securities carried at FMV in the books of account.
                </Alert>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The organization should maintain books and records to
                  substantiate information about any securities or other assets
                  sold for which market quotations weren&apos;t published or
                  weren&apos;t otherwise readily available. The recorded
                  information should include:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    A description of the asset;
                  </li>
                  <li style={{ marginBottom: 12 }}>Date acquired;</li>
                  <li style={{ marginBottom: 12 }}>
                    Whether acquired by donation or purchase;
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Date sold and to whom sold;
                  </li>
                  <li style={{ marginBottom: 12 }}>Gross sales price;</li>
                  <li style={{ marginBottom: 12 }}>
                    Cost, other basis, or, if donated, value at time acquired;
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Expense of sale and cost of improvements made after
                    acquisition; and
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Depreciation since acquisition, if depreciable property.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 8a</span> - Enter in
                  the line the gross income from fundraising events, not
                  including the amount of contributions from fundraising events
                  reported on line 1c.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 8b</span> - Enter on
                  this line both the cost or other basis of any items sold at
                  the events and the expenses that relate directly to the
                  production of the revenue portion of the fundraising activity,
                  whether incurred before, during, or after the event.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> Indirect fundraising expenses, such as certain
                  advertising expenses associated with raising these
                  contributions, must be reported on the appropriate lines in
                  Part IX, column (D), not line 8b.
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 9a</span> - Include in
                  line 9a only the gross income from gaming activities. Don’t
                  include contributions from gaming activities, which should be
                  reported on line 1f.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> Organizations that report more than $15,000 on
                  line 9a must also answer “Yes” on Part IV, line 19, and
                  complete Part III of Schedule G (Form 990).
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 9b</span> - Enter on
                  this line the expenses that relate directly to the production
                  of the revenue portion of the gaming activity.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Direct expenses of gaming include:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>Cash prizes;</li>
                  <li style={{ marginBottom: 12 }}>Noncash prizes;</li>
                  <li style={{ marginBottom: 12 }}>
                    Compensation to bingo callers and workers;
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Rental of gaming equipment; and
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Cost of gaming supplies such as pull tabs, bingo cards, etc.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 10a</span> - Enter the
                  organization&apos;s gross income from sales of inventory
                  items, less returns and allowances.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The organization must report the sales revenue regardless of
                  whether the sales activity is an exempt function of the
                  organization or an unrelated trade or business.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 10b</span> - Enter the
                  cost of goods sold related to the sales of inventory. The
                  usual items included in the cost of goods sold are direct and
                  indirect labor, materials and supplies consumed, freight-in,
                  and a portion of overhead expenses.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Miscellaneous Revenue
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part8-line11a-11e-img.webp" />
                  <source srcSet="/assets/images/f990-part8-line11a-11e-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part8-line11a-11e-img.png"
                    loading="lazy"
                    alt="Form 990 Part VIII, Lines 11a–11e showing miscellaneous revenue"
                    width={760}
                    height={99}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 11a - 11d</span> -
                  Enter all other types of revenue not reportable on lines 1
                  through 10. Enter the three largest sources on lines 11a
                  through 11c and all other revenue on line 11d.
                </Typography>
                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  For each amount entered on lines 11a, 11b, and 11c, the
                  organization must also enter a corresponding business activity
                  code from Business Activity Codes, later.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 12 - Total Revenue
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part8-line12-img.webp" />
                  <source srcSet="/assets/images/f990-part8-line12-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part8-line12-img.png"
                    loading="lazy"
                    alt="Form 990-part-VIII Line 12-Total Revenue "
                    width={760}
                    height={19}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    For column (A), add lines 1h, 2g, 3 through 5, 6d, 7d, 8c,
                    9c, 10c, and 11e.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    For columns (B) through (D), add lines 2a through 2f, 3, 4,
                    5, 6d, 7d, 8c, 9c, 10c, and 11a through 11d.
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The amounts reported on line 12 in columns (B), (C), and (D),
                  plus the amount reported on line 1h, should equal line 12,
                  column (A).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part IX - Statement of Functional Expenses
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part9-line1-10-img.webp" />
                  <source srcSet="/assets/images/f990-part9-line1-10-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part9-line1-10-img.png"
                    loading="lazy"
                    alt="Form 990 Part IX Lines 1–10 grants, compensation & employee expenses"
                    width={760}
                    height={376}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 1</span> - Enter the
                  amount that the organization, at its discretion, paid in
                  grants to domestic organizations and domestic governments.
                  Organizations must also report voluntary grants to state or
                  local affiliates for specific (restricted) purposes or
                  projects on line 1.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the organization reported on line 1 more than $5,000 of
                  grants or other assistance to any domestic organization or any
                  domestic government, the organization must complete Parts I
                  and II of Schedule I (Form 990).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 2</span> - Enter on
                  this line the amount paid by the organization to domestic
                  individuals in the form of scholarships, fellowships,
                  stipends, research grants, and similar payments and
                  distributions.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  While reporting, also include grants and other assistance paid
                  to third-party providers for the benefit of specified domestic
                  individuals.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> If line 2 exceeds $5,000, the organization must
                  complete Parts I and III of Schedule I (Form 990).
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 3</span> - Enter the
                  total amount of grants and other assistance made to foreign
                  organizations, foreign governments, and foreign individuals,
                  and to domestic organizations or domestic individuals to
                  provide grants or other assistance to designated foreign
                  organizations or foreign individuals.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the entered amount exceeds $5,000, the organization may
                  have to complete Part II and/or Part III of Schedule F (Form
                  990), Statement of Activities Outside the United States.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 4</span> - Enter the
                  payments made by the organization to provide benefits to
                  members (such as payments made by an organization exempt under
                  section 501(c)(8), 501(c)(9), or 501(c)(17) to obtain
                  insurance benefits for members, or patronage dividends paid by
                  section 501(c)(12) organizations to their members).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  However, don&apos;t report on this line the cost of
                  employment-related benefits such as health insurance, life
                  insurance, or disability insurance provided by the
                  organization to its officers, directors, trustees, key
                  employees, and other employees.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 5</span> - Enter the
                  total compensation paid to current officers, directors,
                  trustees, and key employees (as defined under Part VII,
                  earlier) for the organization&apos;s tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report in this line all compensation amounts relating to such
                  an individual, including those related to services performed
                  in a capacity other than as an officer, director, trustee, or
                  key employee.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 6</span> - Section
                  501(c)(3), 501(c)(4), and 501(c)(29) organizations must report
                  the total compensation and other distributions provided to
                  disqualified persons and persons described in section
                  4958(c)(3)(B) to the extent not included on line 5.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 7</span> - Enter the
                  total amount of employee salaries, wages, fees, bonuses,
                  severance payments, and similar amounts paid or provided by
                  the filing organization, common paymasters, and
                  payroll/reporting agents in return for services rendered to
                  the filing organization that aren&apos;t reported on line 5 or
                  6.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 8</span> - Enter the
                  employer&apos;s share of contributions to, or accruals under,
                  qualified and nonqualified pension and deferred compensation
                  plans for the year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The organization should include contributions made by the
                  filing organization, common paymasters, and payroll/reporting
                  agents to the filing organization&apos;s sections 401(k) and
                  403(b) pension plans on behalf of employees.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  However, it shouldn&apos;t include contributions to qualified
                  pension, profit-sharing, and stock bonus plans under section
                  401(a) solely for the benefit of current or former officers,
                  directors, trustees, key employees, or disqualified persons,
                  which are reportable on line 5 or 6.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 9</span> - Enter
                  contributions by the filing organization, common paymasters,
                  and payroll/reporting agents to the filing organization&apos;s
                  employee benefits programs (such as insurance, health, and
                  welfare programs that aren&apos;t an incidental part of a
                  pension plan included on line 8), and the cost of other
                  employee benefits.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  While reporting the contributions, don&apos;t include
                  contributions on behalf of current or former officers,
                  directors, trustees, key employees, or other persons that were
                  included on lines 5 or 6.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 10</span> - Enter the
                  amount of federal, state, and local payroll taxes for the year
                  but only those taxes that are imposed on the organization as
                  an employer. This includes the employer&apos;s share of social
                  security and Medicare taxes, the federal unemployment tax
                  (FUTA), state unemployment compensation taxes, and other state
                  and local payroll taxes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  However, don&apos;t include on line 10 taxes withheld from
                  employees&apos; salaries and paid to various governmental
                  units such as federal, state, and local income taxes and the
                  employees&apos; shares of social security and Medicare taxes;
                  such withheld amounts must be reported as compensation.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part9-line11-23-img.webp" />
                  <source srcSet="/assets/images/f990-part9-line11-23-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part9-line11-23-img.png"
                    loading="lazy"
                    alt="Form 990 Part IX Lines 11–23 itemized functional expenses"
                    width={760}
                    height={364}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 11</span> - Enter on
                  lines 11a through 11g the amounts for services provided by
                  independent contractors for management, legal, accounting,
                  lobbying, professional fundraising services, investment
                  management, and other services, respectively.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  However, don&apos;t include on line 11 amounts paid to or
                  earned by employees, officers, directors, trustees, or
                  disqualified persons for these types of services, which must
                  be reported on lines 5 through 7.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 11a</span> - Enter the
                  total fees charged for management services provided by outside
                  firms and individuals.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 11b</span> - Enter on
                  this line the total legal fees charged by outside firms and
                  individuals. Also, report any amounts for lobbying services
                  provided by attorneys on line 11d.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  However, don&apos;t include any penalties, fines, settlements,
                  or judgments imposed against the organization as a result of
                  legal proceedings. Report those expenses on line 24.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 11c</span> - Enter on
                  line 11c the total accounting and auditing fees charged by
                  outside firms and individuals.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 11d</span> - Enter
                  amounts for activities intended to influence foreign,
                  national, state, or local legislation, including direct
                  lobbying and grassroots lobbying.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 11e</span> - Enter on
                  this line the amounts paid for professional fundraising
                  services, including solicitation campaigns and advice or other
                  consulting services supporting in-house fundraising campaigns.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 11f</span> - Report on
                  this line the amounts for investment counseling and portfolio
                  management.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  However, don&apos;t include transaction costs such as
                  brokerage fees and commissions, which are considered sales
                  expenses and are included in Part VIII, line 7b.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 11g</span> - Enter
                  amounts for other independent contractor services not listed
                  on lines 11a through 11f.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> If the amount on line 11g exceeds 10% of the
                  amount in line 25, column (A), the organization must list the
                  type and amount of each line 11g expense on Schedule O (Form
                  990).
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 12</span> - Enter on
                  line 12 the amounts paid for advertising. Include amounts for
                  print and electronic media advertising. While reporting those
                  expenses, also include the Internet site link costs, signage
                  costs, and advertising costs for the organization&apos;s
                  in-house fundraising campaigns.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 13</span> - Enter here
                  the amounts for supplies (office, classroom, or other
                  supplies); telephone (cell phones and landlines) and
                  facsimile; postage (overnight delivery, parcel delivery,
                  trucking, and other delivery expenses) and mailing expenses;
                  shipping materials; equipment rental; bank fees; and other
                  similar costs.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Also, include on this line the printing costs of a general
                  nature.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 14</span> - In line
                  14, enter the amounts for information technology, including
                  hardware, software, and support services such as maintenance,
                  help desk, and other technical support services.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 15</span> - Enter on
                  this line the amounts for royalties, license fees, and similar
                  amounts that allow the organization to use intellectual
                  property such as patents and copyrights.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 16</span> - Enter
                  amounts for the use of office space or other facilities,
                  including rent; heat, light, power, and other utility
                  expenses; property insurance; real estate taxes; mortgage
                  interest; and similar occupancy-related expenses.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  However, don&apos;t include on line 16 expenses reported as
                  office expenses (such as telephone expenses) on line 13.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 17</span> - Enter on
                  line 17 the total travel expenses, including transportation
                  costs, meals, lodging, and per diem payments. Travel costs
                  include the expenses of purchasing, leasing, operating, and
                  repairing any vehicles owned by the organization and used for
                  the organization&apos;s activities.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  However, if the organization leases vehicles on behalf of its
                  executives or other employees as part of an executive or
                  employee compensation program, the leasing costs are
                  considered employee compensation and are reported on lines 5
                  through 7.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 18</span> - Enter
                  total amounts for travel or entertainment expenses (including
                  reimbursement for such costs) for any federal, state, or local
                  public officials (as determined under section 4946(c)) and
                  their family members (as determined under section 4946(d)).
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> The organization is responsible for keeping
                  records of all travel and entertainment expenses related to a
                  government official whether or not the expenses are reported
                  on line 18 or line 24.
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 19</span> - Enter the
                  total expenses incurred by the organization in conducting
                  meetings related to its activities. While reporting expenses,
                  include facility rentals, speakers&apos; fees and expenses,
                  and printed materials. Include the registration fees (but not
                  travel expenses) paid for sending any of the
                  organization&apos;s staff to conferences, conventions, and
                  meetings conducted by other organizations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 20</span> - Enter the
                  total interest expense for the year and don&apos;t include any
                  interest attributable to rental property (reported on Part
                  VIII, line 6b) or any mortgage interest (reported as an
                  occupancy expense on line 16).
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>Lines 21</span> - Enter the
                  types of payments to organizations affiliated with (closely
                  related to) the filing organization such as
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Payments to affiliated state or national organizations
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Purchases from affiliates
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Expenses for providing goods or services to affiliates
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Voluntary awards or grants to affiliates
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Membership dues paid to other organizations.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 22</span> - If your
                  organization records depreciation, depletion, amortization, or
                  similar expenses, enter the total on line 22. Also, include
                  any depreciation or amortization of leasehold improvements and
                  intangible assets.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 23</span> - Enter on
                  this line the total insurance expenses other than insurance
                  attributable to rental property (reported on Part VIII, line
                  6b). Do not report on this line payments made by organizations
                  exempt under section 501(c)(8), (9), or (17) to obtain
                  insurance benefits for members. Report those expenses on line
                  4.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 24. Other expenses
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part9-line24-26-img.webp" />
                  <source srcSet="/assets/images/f990-part9-line24-26-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part9-line24-26-img.png"
                    loading="lazy"
                    alt="Form 990 Part IX Lines 24–26 other expenses, total functional expenses & joint costs"
                    width={760}
                    height={229}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter on line 24 the types and amounts of expenses that
                  weren&apos;t reported on lines 1 through 23. Don&apos;t
                  include a separate entry for “miscellaneous expenses,”
                  “program expenses,” “other expenses,” or a similar general
                  category on lines 24a-d. List them on line 24e.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If line 24e amount exceeds 10% of line 25, column (A), amount,
                  list line 24e expenses on Schedule O.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The organization must separately report the amount, if any, of
                  unrelated business income taxes that it paid or accrued during
                  the tax year on line 24.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 25 – Total Functional Expenses
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>total of all expenses</span>{' '}
                  reported on Lines 1 through 24e. This amount includes{' '}
                  <span style={{ fontWeight: 600 }}>
                    program, management & general, and fundraising expenses
                  </span>{' '}
                  for the year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 26</span> - To
                  complete this line, organizations that included in program
                  service expenses (column (B) of Part IX) any joint costs from
                  a combined educational campaign and fundraising solicitation
                  must disclose how the total joint costs of all such combined
                  activities were allocated in Part IX between education and
                  fundraising.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part X - Balance Sheet
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part10-columnsa-b-img.webp" />
                  <source srcSet="/assets/images/f990-part10-columnsa-b-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part10-columnsa-b-img.png"
                    loading="lazy"
                    alt="Form 990 Part X Columns A–B beginning & end-of-year balance sheet"
                    width={760}
                    height={67}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  All organizations filing Form 990 must complete Part X,
                  Balance Sheet. This part contains 2 columns
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Column (A) - Beginning of year</b>. In column (A), enter
                  the amount from the preceding year&apos;s Form 990, column
                  (B). If the organization was excepted from filing Form 990 for
                  the preceding year, enter amounts the organization would have
                  entered in column (B) for that year. If this is the
                  organization&apos;s first year of existence, enter zeros on
                  lines 16, 26, 32, and 33 in column (A).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Column (B) - End of year</b>. When Schedule D (Form 990)
                  reporting is required for any item in Part X, it is only for
                  the end-of-year balance sheet figure reported in column (B).
                  If this is the organization&apos;s final return, enter zeros
                  on lines 16, 26, 32, and 33 in column (B).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  No substitute balance sheet will be accepted.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Assets
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part10-line1-16-img.webp" />
                  <source srcSet="/assets/images/f990-part10-line1-16-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part10-line1-16-img.png"
                    loading="lazy"
                    alt="Form 990-part-X Line 1 – 16 showing the organization’s assets"
                    width={760}
                    height={339}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 1</span> - Enter on
                  this line the total funds that the organization has in cash,
                  including amounts held as “petty cash” at its offices or other
                  facilities, and amounts held in banks in non-interest-bearing
                  accounts.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  However, don&apos;t include cash balances held in an
                  investment account with a financial institution and reported
                  on lines 11 through 13.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 2</span> - Enter in
                  line 2 the combined total of amounts held in interest-bearing
                  checking and savings accounts, deposits in transit, temporary
                  cash investments, and U.S. Treasury bills or other
                  governmental obligations that mature in less than a year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Don&apos;t include cash balances held in an investment account
                  with a financial institution and reported on lines 11 through
                  13.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Also, don&apos;t include advances to employees or officers or
                  refundable deposits paid to suppliers or other independent
                  contractors. Report the income from those investments in Part
                  VIII, line 3.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 3</span> - Enter the
                  total of (a) all pledges receivable, less any amounts
                  estimated to be uncollectible, including pledges made by
                  officers, directors, trustees, key employees, and highest
                  compensated employees; and (b) all grants receivable.
                </Typography>
                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  Organizations that follow ASC 958 can report the present value
                  of the grants receivable as of each balance sheet date.
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 4</span> - Enter on
                  this line the organization&apos;s total accounts receivable
                  (reduced by any allowance for doubtful accounts) from the sale
                  of goods and the performance of services.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 5</span> - Report on
                  line 5 loans and other receivables due from current or former
                  officers, directors, trustees, key employees, and creator or
                  founder, substantial contributor, or 35% controlled entity or
                  family member of any of these persons.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 6</span> - If
                  you&apos;re a Section 501(c)(3), 501(c)(4), and 501(c)(29)
                  organization, you must report on line 6 receivables due from
                  other disqualified persons (for purposes of section 4958, see
                  Appendix G), and from persons described in section
                  4958(c)(3)(B). Include all amounts owed on secured and
                  unsecured loans made to such persons. Report interest from
                  such receivables on Part VIII, line 11.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> Don&apos;t report on line 5 or 6 (a) pledges or
                  grants receivable, which are to be reported on line 3; or (b)
                  receivables that are excepted from reporting on Schedule L
                  (Form 990), Part II (except for excess benefit transactions
                  involving receivables)
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 7</span> - Enter on
                  this line the net amount of all notes receivable and loans
                  receivable not listed on lines 5 and 6, including receivables
                  from unrelated third parties.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> The term “unrelated third parties” includes
                  independent contractors providing goods or services and
                  employees who aren&apos;t current or former officers,
                  directors, trustees, key employees, highest compensated
                  employees, or disqualified persons.
                </Alert>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  However, don&apos;t include the following in line 7
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Receivables reported on line 4.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Program-related investments reported on line 13.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Notes receivable acquired as investments reported on line
                    12.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 8</span> - Enter on
                  line 8 the amount of materials, goods, and supplies held for
                  future sale or use, whether purchased, manufactured by the
                  organization, or donated.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 9</span> - In line 9,
                  enter the amount of short-term and long-term prepayments of
                  expenses attributable to one or more future accounting
                  periods. Examples of such expenses include prepayments of
                  rent, insurance, or pension costs, and expenses incurred for a
                  solicitation campaign to be conducted in a future accounting
                  period.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 10a</span> - Enter on
                  this line the cost or other basis of all land, buildings,
                  equipment, and leasehold improvements held at the end of the
                  year. Also, include both property held for investment purposes
                  and property used for the organization&apos;s exempt
                  functions.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If an amount is reported on line 10a, answer “Yes” on Part IV,
                  line 11a, and complete Schedule D (Form 990), Part VI. The
                  amount reported on line 10a must equal the total of Schedule
                  D, Part VI, columns (a) and (b).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 10b</span> - Enter on
                  line 10b the total amount of accumulated depreciation for the
                  assets reported on line 10a. The amount reported on line 10b
                  must equal the total of Schedule D (Form 990), Part VI, column
                  (c).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    Lines 10c. Column (A) - Beginning of year
                  </span>{' '}
                  - Enter the cost or other basis of land, buildings, and
                  equipment, net of any accumulated depreciation, as of the
                  beginning of the year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>
                    Line 10c. Column (B) - End of year
                  </span>{' '}
                  - Enter line 10a minus line 10b. The amount reported must
                  equal the total of Schedule D (Form 990), Part VI, column (d).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 11</span> - Enter on
                  line 11 the total value of publicly traded securities held by
                  the organization as investments.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This includes common and preferred stocks, bonds (including
                  governmental obligations such as bonds and Treasury bills),
                  and mutual fund shares that are listed and regularly traded in
                  an over-the-counter market or an established exchange and for
                  which market quotations are published or are otherwise readily
                  available.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  However, don&apos;t report on line 11 publicly traded stock
                  for which the organization holds 5% or more of the outstanding
                  shares of the same class or publicly traded stock in a
                  corporation that comprises more than 5% of the
                  organization&apos;s total assets. Those investments must be
                  reported on line 12.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 12</span> - Enter on
                  this line the total value of all securities, partnerships, or
                  funds that aren&apos;t publicly traded. This includes stock in
                  a closely held company whose stock isn&apos;t available for
                  sale to the general public or which isn&apos;t widely traded.
                  Don&apos;t include program-related investments in line 12.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 13</span> - In line
                  13, report the total book value of all investments made
                  primarily to accomplish the organization&apos;s exempt
                  purposes rather than to produce income.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Examples of program-related investments include student loans
                  and notes receivable from other exempt organizations that
                  obtained the funds to pursue the filing organization&apos;s
                  exempt function.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 14</span> - Report on
                  this line the total value of all non-monetary, non-physical
                  assets such as copyrights, patents, trademarks, mailing lists,
                  or goodwill.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>Lines 15</span> - Report on
                  this line the total book value of all assets held and not
                  reported on lines 1 through 14.
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    If an amount is reported on this line that is 5% or more of
                    the amount reported on Part X, line 16, answer “Yes” on Part
                    IV, line 11d, and complete Schedule D (Form 990), Part IX.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    The amount reported in Part X, line 15, column (B), must
                    equal the total of Schedule D, Part IX, column (b).
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 16</span> - The Total
                  Assets calculated on line 16 must equal the amounts on line 33
                  for both the beginning and end of the year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Liabilities
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part10-line7-26-img.webp" />
                  <source srcSet="/assets/images/f990-part10-line7-26-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part10-line7-26-img.png"
                    loading="lazy"
                    alt="Form 990 Part X, Lines 17–26 showing the organization’s liabilities"
                    width={760}
                    height={223}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 17</span> - Enter the
                  total of accounts payable to suppliers, service providers,
                  property managers, and other independent contractors, plus
                  accrued expenses such as salaries payable, accrued payroll
                  taxes, and interest payable.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you’re a Section 501(c)(21) trust, include accrued trustee
                  fees, etc. However, do not include the present value of
                  payments for approved claims, or the estimated liability for
                  future claims.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 18</span> - Enter on
                  line 18 the unpaid portion of grants and awards that the
                  organization has committed to pay other organizations or
                  individuals, whether or not the commitments have been
                  communicated to the grantees.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you’re a Section 501(c)(21) trust, include payments for
                  approved black lung claims that are due but not paid. However,
                  do not include amounts for black lung claims being contested.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 19</span> - Report on
                  line 19 the revenue that the organization has received but not
                  yet earned as of the balance sheet date under its method of
                  accounting.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 20</span> - Enter the
                  amount of tax-exempt bonds (or other obligations) for which
                  the organization has a direct or indirect liability that was
                  either issued by the organization on behalf of a state or
                  local governmental unit, or by a state or local governmental
                  unit on behalf of the organization, and for which the
                  organization has a direct or indirect liability.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 21</span> - Enter the
                  amount of funds or other assets held in an escrow or custodial
                  account for other individuals or organizations. Enter these
                  amounts only if the related assets (such as cash) are reported
                  on lines 1 through 15 of this part.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 22</span> - Enter on
                  line 22 the unpaid balance of loans and other payables
                  (whether or not secured) to current and former officers,
                  directors, trustees, key employees, creator or founder,
                  substantial contributor, or 35% controlled entity or family
                  member of any of these persons, and persons described in
                  section 4958(c)(3)(B).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Don&apos;t report on line 22 accrued but unpaid compensation
                  owed by the organization, and loans and payables excepted from
                  reporting on Schedule L (Form 990), Part II (except for excess
                  benefit transactions involving receivables).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 23</span> - On line
                  23, enter the total amount of secured mortgages and notes
                  payable to unrelated third parties that are secured by the
                  organization&apos;s assets as of the end of the tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 24</span> - Enter the
                  total amount of notes and loans that are payable to unrelated
                  third parties but aren&apos;t secured by the
                  organization&apos;s assets.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 25</span> - Enter the
                  total amount of all liabilities not properly reportable on
                  lines 17 through 24. Items properly reported on this line
                  include federal income taxes payable and secured or unsecured
                  payables to related organizations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The organization must also answer “Yes” on Part IV, line 11e,
                  and complete Schedule D (Form 990), Part X.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 26</span> - To
                  calculate the Total Liabilities, add the totals in columns (A)
                  and (B), lines 17 through 25.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Net Assets and Fund Balances
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part10-line27-33-img.webp" />
                  <source srcSet="/assets/images/f990-part10-line27-33-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part10-line27-33-img.png"
                    loading="lazy"
                    alt="Form 990 Part X Lines 27–33 net assets and fund balances"
                    width={760}
                    height={183}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Organizations that follow ASC 958*
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  *If your organization follows ASC 958, check the box above
                  line 27, and complete lines 27 through 28 and lines 32 and 33.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 27</span> - Enter the
                  balance per book of net assets without donor restrictions. All
                  funds without donor-imposed restrictions must be reported on
                  line 27, regardless of the existence of any board designations
                  or appropriations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 28</span> - Enter on
                  line 28 the balance per book of net assets with donor
                  restrictions.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Organizations that don’t follow ASC 958*
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  *If your organization doesn&apos;t follow ASC 958, check the
                  box above line 29 and complete lines 29 through 33.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 29 - Capital stock or trust principal, or current funds
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    If you’re a corporation, enter the balance per book of
                    capital stock accounts. Show par or stated value (or for
                    stock with no par or stated value, total amount received on
                    issuance) of all classes of stock issued and not yet
                    canceled.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    If you’re a trust, enter the amount in the trust principal
                    or corpus.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    If you’re an organization using the fund method of
                    accounting, enter the fund balances for the
                    organization&apos;s current restricted and unrestricted
                    funds.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 30</span> - Enter the
                  balance of paid-in capital over par or stated value for all
                  stock issued and not yet canceled, as recorded on the
                  corporation&apos;s books.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If stockholders or others made donations that the organization
                  records as paid-in capital, include them here. Also, enter the
                  fund balance for the land, building, and equipment fund on
                  this line.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 31 - Retained earnings, endowment, accumulated income, or
                  other funds.
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    If you’re a corporation, enter the balance of retained
                    earnings as recorded on the corporation&apos;s books, or
                    similar account, minus the cost of any corporate treasury
                    stock.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    If you’re a trust, enter the balance in the accumulated
                    income or similar account.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    If you’re an organization using the fund method of
                    accounting, enter the total of the fund balances for the net
                    assets without donor restrictions funds, and the net assets
                    with donor restrictions funds, as well as balances of any
                    other funds not reported on lines 29 and 30.
                  </li>
                </ul>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 32 - Total net assets or fund balances.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you’re an organization that follows ASC 958, enter the
                  total of lines 27 through 28. If not, enter the total of lines
                  29 through 31.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 33</span> - To
                  calculate Total liabilities and net assets/fund balances,
                  enter the total of line 26 and line 32. This amount must equal
                  the amount on line 16.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part XI - Reconciliation of Net Assets
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part11-line1-10-img.webp" />
                  <source srcSet="/assets/images/f990-part11-line1-10-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part11-line1-10-img.png"
                    loading="lazy"
                    alt="Form 990 Part XI Lines 1–10 net assets reconciliation"
                    width={760}
                    height={215}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 1 - Total Revenue
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In this line, enter the amount of total revenue reported in
                  Part VIII, line 12, column (A).
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 2 - Total Expenses
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In this line, enter the amount of total expenses reported in
                  Part IX, line 25, column (A)
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 4 - Net assets or fund balances at the beginning of the
                  year
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In this line, enter the amount of net assets or fund balances
                  at the beginning of the year reported in Part X, line 32,
                  column (A).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The amount entered here should be the same amount reported in
                  Part X, line 33, column (B), for the prior year’s return.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 5 - Net unrealized gains (losses) on investments
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report here the net unrealized gains or losses on investments
                  reported in the organization&apos;s audited financial
                  statements (or other financial statements).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This amount represents the change in the market value of
                  investments that weren&apos;t sold or exchanged during the tax
                  year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 6 - Donated services and use of facilities
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In this line, report the value of services or use of
                  facilities donated to the organization (net of services or use
                  of facilities donated by the organization) reported as income
                  or expense in the financial statements.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Lines 7</span> - Enter the
                  organization’s investment expenses made for the tax year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 8 - Prior period adjustments
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Prior period adjustments are corrections of errors in
                  financial statements of prior years, or changes in accounting
                  principles applied to such years
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In line 8, report the net prior period adjustments during the
                  tax year reported in the financial statements.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 9 - Other changes in net assets or fund balances
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In this line, enter the total amount of other changes in net
                  assets or fund balances during the year. Also, explain these
                  changes on Schedule O (Form 990)
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> Amounts to report here include losses on
                  uncollectible pledges, refunds of contributions and program
                  service revenue, reversal of grant expenses, any difference
                  between FMV and a book value of property given as an award or
                  grant, and any other changes in net assets or fund balances
                  not listed on lines 5–8.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 10 - Net assets or fund balances at the end of the year
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  To calculate the Net assets or fund balances at the end of the
                  year, add the amounts on lines 3 through 9. The total must
                  equal the amount reported in Part X, line 32, column (B).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part XII - Financial Statements and Reporting
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-part12-line1-3b-img.webp" />
                  <source srcSet="/assets/images/f990-part12-line1-3b-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-part12-line1-3b-img.png"
                    loading="lazy"
                    alt="Form 990 Part XII, Lines 1–3b covering financial statements, audits & reporting requirements "
                    width={760}
                    height={360}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 1 - Accounting method
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select the accounting method the organization used to complete
                  its Form 990 return. It can be <i>Cash, Accrual, or Other</i>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the organization changed its method of accounting from a
                  prior year or checked “Other,” explain the change on{' '}
                  <b>
                    <i>Schedule O (Form 990)</i>
                  </b>
                  .
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 2 - Financial statements and independent accountant
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 2a</span> - Select “
                  <b>Yes</b>” if the organization’s financial statements were{' '}
                  <b>compiled</b> or <b>reviewed</b> by an independent
                  accountant.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If selected <b>Yes</b>, indicate whether the financial
                  statements for the year were compiled or reviewed on a
                  separate basis, consolidated basis, or both.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 2b</span> - Select “
                  <b>Yes</b>” if the organization’s financial statements were{' '}
                  <b>audited</b> by an independent accountant.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If selected <b>Yes</b>, indicate whether the financial
                  statements for the year were compiled or reviewed on a
                  separate basis, consolidated basis, or both.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Lines 2c</span> - If you
                  have selected “Yes” to line 2a or 2b, indicate whether the
                  organization has a committee that is responsible under its
                  governing documents or through delegation by its governing
                  body for
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  (i) overseeing the compilation, review, or audit of the
                  financial statements; and
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  (ii) the selection of an independent accountant who compiled,
                  reviewed, or audited the statements
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select“Yes” to line 2c only if both (i) and (ii) apply. If
                  this process has changed from the prior year, describe on
                  Schedule O (Form 990).
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> An accountant is independent if he or she meets
                  the standards of independence set forth by the American
                  Institute of Certified Public Accountants (AICPA), the Public
                  Company Accounting Oversight Board (PCAOB), or another similar
                  body that oversees or sets standards for the accounting or
                  auditing professions.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 3a - Uniform Guidance, 2 C.F.R. Part 200, Subpart F
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select “Yes” if, during the year, the organization was
                  required under the Uniform Guidance, 2 C.F.R. Part 200,
                  Subpart F, to undergo an audit or audits because of its
                  receipt of federal contract awards.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> The Uniform Guidance, 2 C.F.R. Part 200,
                  Subpart F, requires states, local governments, and nonprofit
                  organizations that spend $750,000 or more of federal awards in
                  a year to obtain an annual audit.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 3b - Required audits
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you have selected “Yes” on line 3a, select “Yes” on line 3b
                  if the audit was completed or in progress during the
                  organization&apos;s tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the answer to line 3b is “No,” explain on Schedule O (form
                  990 irs) why the organization hasn&apos;t undergone any
                  required audits and describe any steps taken to undergo such
                  audits.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to file Form 990?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You have two options to file your Form 990 return with the IRS
                  - efficient e-filing or traditional paper filing.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Form 990 E-filing (recommended)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS recommends e-filing for a{' '}
                  <b>faster, simpler, and more secure</b> experience. Plus,
                  you&apos;ll get <b>instant confirmation</b> when your return
                  is processed, saving you time and anxiety.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Choose TaxZerone, an <b>IRS-authorized e-file provider</b>, to
                  make your{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 990 electronic filing
                  </Link>{' '}
                  process a breeze. Our user-friendly platform lets you complete
                  your return in <b>just a few clicks</b>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Focus on your mission, not your paperwork. Get started today
                  with TaxZerone for a smooth and efficient filing experience.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you choose to paper-file Form 990, here are the steps you
                  need to follow
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Obtain the Form:</b> Download and print Form 990 from the
                    IRS website.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b> Complete Form:</b> Fill out each section of Form 990,
                    providing accurate information about your
                    organization&apos;s activities, finances, governance, and
                    compliance with tax laws. Use the instructions provided by
                    the IRS to guide you through the process.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b> Attach Schedules if Necessary:</b> Depending on your
                    organization&apos;s activities and financial transactions,
                    you may need to attach additional schedules or supporting
                    documents to Form 990.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b> Calculate and Include Payment:</b> If your organization
                    owes taxes based on the information provided on Form 990,
                    calculate the amount owed and include payment with the
                    submission. Payments can be made by check or money order
                    payable to the &quot;United States Treasury.&quot;
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b> Sign and Date the Form:</b> The form must be signed and
                    dated by an authorized individual within the organization,
                    such as the president, treasurer, or another authorized
                    officer.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b> Mail the Form:</b> Once completed, mail the signed Form
                    990 and any accompanying documents to the IRS mailing
                    address.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b> Keep Copies for Your Records:</b> Make copies of the
                    completed Form 990 and all supporting documents for your
                    organization&apos;s records. These documents may be needed
                    for future reference or in the event of an audit.
                  </li>
                </ul>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note :</b> While the IRS previously allowed paper filing
                  for Form 990, there has been a change in recent years. As for
                  tax years ending after July 31, 2020, electronic filing is
                  mandatory for all Form 990 submissions.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to send Form 990?
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Send a paper copy of Form 990 to the mailing address mentioned
                  below:
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Department of the Treasury,
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Internal Revenue Service Center,
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Ogden, UT 84201-0027.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  If your organization’s principal business, office, or agency
                  is located in a foreign country or a U.S. possession, mail the
                  form to:
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Internal Revenue Service Center,
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  P.O. Box 409101,
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Ogden, UT 84409.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to e-file Form 990?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  E-filing your Form 990 takes just 3 steps! To make it even
                  faster, gather all the necessary information (like
                  organization details and compensation amounts) before you
                  start. That way, you can complete your filing process in no
                  time.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Information required to file Form 990
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Organization&apos;s details, EIN, tax year, accounting method,
                  and exempt status. Once you have this information ready, you
                  can follow the steps below to e-file Form 990 using TaxZerone.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 1</b> - Enter the required information, such as your
                  organization&apos;s details, EIN, tax year, accounting method,
                  and exempt status.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 2</b> - Complete all the parts provided in the form
                  and attach the required schedules.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <b>Step 3</b> - Review the information you provided in the
                  form, check and rectify the errors if any, and transmit the
                  Form 990 return to the IRS.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  E-file Form 990 with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  When you choose to{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file Form 990
                  </Link>{' '}
                  with TaxZerone, you can enjoy benefits such as
                </Typography>
                <List sx={{ mb: 1 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>IRS-authorized:</b> File your 990 return through an
                      IRS-authorized e-file service provider.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>IRS form validations:</b> Catch and rectify any errors
                      before you submit.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Easy E-filing:</b> Simple navigating and a
                      user-friendly interface to e-file Form 990 return within
                      minutes.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Secure E-filing:</b> Your 990 return will be submitted
                      securely to the IRS.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b> Top value:</b> Enjoy the industry&apos;s best price,
                      starting at just $179.99 per return
                    </ListItemText>
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to E-file your Form 990?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              TaxZerone makes it simple and secure to e-file your return with
              the IRS—avoid delays,
              <br /> meet your{' '}
              <Link
                href="/resources/tax-exempt/form-990-due-dates/"
                sx={{ color: '#0202ff' }}
              >
                {' '}
                Form 990 deadline
              </Link>
              , and stay on track.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990instructions`}
              >
                E- file Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form990Instructions;
